import type { ServiceAreaFaq, ServiceAreaSlug } from "@/config/service-areas";
import type { ServicePage } from "@/config/services";

type ServiceSlug =
  | "emergency-roof-leak-repair"
  | "flashing-roof-penetration-repair"
  | "moss-removal-roof-gutter"
  | "asphalt-shingle-replacement"
  | "roof-health-check-maintenance"
  | "shingle-spot-repair"
  | "gutter-cleaning-downspout-flush"
  | "storm-tarping-emergency-dry-in"
  | "skylight-leak-flashing-repair"
  | "chimney-flashing-repair"
  | "gutter-replacement"
  | "gutter-guard-installation"
  | "roof-ventilation-upgrade"
  | "fascia-soffit-repair-replacement"
  | "cedar-shake-repair-restoration"
  | "flat-low-slope-roof-repair"
  | "metal-roof-repair-maintenance"
  | "cedar-shake-roof-replacement"
  | "metal-roof-replacement"
  | "flat-roof-replacement-sbs-tpo-pvc"
  | "roof-replacement"
  | "roof-repair"
  | "asphalt-shingle-roofing"
  | "metal-roofing"
  | "flat-roofing"
  | "roof-inspection-maintenance";

type ServiceContent = Partial<Omit<ServicePage, "slug" | "image">>;
type ServiceAreaContent = {
  intro: string;
  localContext: string;
  assessmentFocus: readonly string[];
  propertyContexts: readonly string[];
  faqs: readonly ServiceAreaFaq[];
};

export const serviceContent = {
  "emergency-roof-leak-repair": {
    title: "緊急屋頂漏水維修",
    shortTitle: "緊急漏水維修",
    eyebrow: "主動進水",
    description:
      "優先漏水診斷：在排期允許時當日到場，針對水從大溫哥華住宅屋頂系統進入的情況，進行臨時或永久性局部維修。",
    definition:
      "緊急屋頂漏水維修是針對屋頂覆蓋層、泛水、穿透點或相關接口處主動或快速發展的進水，進行重點排查與穩定處理。在條件允許時，工作可包括臨時防水和永久性局部維修。",
    forWhom:
      "出現持續滴水、雨天突發天花水漬、暴風雨後閣樓受潮，或需要在規劃更大範圍工程前進行安全診斷和控制的業主。",
    warningSigns: [
      "降雨期間或之後持續滴水或潮濕",
      "天花、牆體或燈具上出現新的或擴大的水漬",
      "保溫層、屋面板或閣樓構架受潮",
      "水沿天溝、煙囪、天窗或管道通風口蔓延",
      "每次天氣事件後室內損壞持續加重",
    ],
    evaluates: [
      "室內證據及可能的水流路徑",
      "受影響區域周邊的屋面覆蓋層",
      "泛水、穿透點、天溝和牆體接口",
      "臨時保護還是永久維修的需求",
      "相鄰材料能否支持耐久施工",
    ],
    causes: [
      "泛水或管道防水套密封失效",
      "被風掀起或缺失的覆蓋材料",
      "雜物堵塞排水導致積水",
      "暴風雨損壞屋脊、邊緣或過渡部位",
      "以往修補未處理真正源頭",
    ],
    methodology: [
      "安全記錄室內和室外證據",
      "追查最可能的進水路徑，而不只是水漬位置",
      "在天氣或進場條件需要時以臨時防水穩定",
      "在周邊組件仍可使用時，以兼容材料進行局部永久維修",
      "說明殘餘風險及是否應進行更大範圍工程",
    ],
    materials: [
      "與現有系統兼容的覆蓋材料",
      "在適當位置的墊層或膜材補丁",
      "泛水金屬件和穿透防水套",
      "密封膠僅作為正確細部的一部分，而非唯一修復",
      "需要時的臨時防水布或幹鋪材料",
    ],
    bcClimate:
      "大溫哥華頻繁降雨意味著小缺陷也會反覆造成室內損壞。及時診斷比表面修補、卻未解決真正路徑更為重要。",
    whenAppropriate: "當進水侷限、周邊屋頂仍可正常使用，且能安全執行耐久維修或臨時防水時。",
    whenOtherService:
      "覆蓋層大範圍失效、多處獨立漏水，或屋頂系統已接近末期時，可能需要更廣泛的維修規劃、僅做暴風雨臨時防水，或討論整體更換。",
    process: [
      "致電或發送您所觀察到的情況及照片",
      "分診與排期：在有空檔時當日到場",
      "在屋頂上溯源診斷，並記錄室內和室外情況",
      "按條件允許進行臨時防水或局部永久維修",
      "書面總結髮現、已完成工作及建議的後續步驟",
    ],
    solutionCategories: [
      {
        title: "源頭診斷",
        body: "將室內證據與最可能進水的室外細部聯繫起來，包括覆蓋層下方的橫向水流。",
      },
      {
        title: "臨時防水",
        body: "在暴風雨損壞後，或永久材料無法在同一次上門完成時，穩定開口部位。",
      },
      {
        title: "局部永久維修",
        body: "以與周邊屋頂系統兼容的材料，更換或重新細部處理失效構件。",
      },
    ],
    repairVsReplace: {
      repair: "當已確定單一失效區域且周邊材料仍足夠完好、可接受兼容施工時，通常適合重點維修。",
      replace: "當檢查發現大範圍老化、多處獨立失效，或組件無法支持耐久補丁時，更換會進入討論。",
    },
    scopeFactors: [
      "進場條件和屋頂高度",
      "天氣窗口",
      "隱蔽損壞範圍",
      "材料兼容性",
      "涉及的穿透點數量",
      "臨時保護需求",
    ],
    faqs: [
      {
        question: "什麼是緊急屋頂漏水維修？",
        answer:
          "緊急屋頂漏水維修是針對屋頂覆蓋層、泛水、穿透點或相關接口處主動或快速發展的進水，進行重點排查與穩定處理。在條件允許時，工作可包括臨時防水和永久性局部維修。",
      },
      {
        question: "下雨時還能修屋頂漏水嗎？",
        answer:
          "在持續降雨時，安全的第一步通常是臨時防水：鋪防水布或密封進水區域，因為大多數永久屋面材料需要乾燥條件才能正確粘結。永久維修隨後在合適的天氣窗口完成。",
      },
      {
        question: "為什麼天花水漬離可見屋頂損壞很遠？",
        answer:
          "水常從一個細部進入，再沿墊層、結構構件或屋面板移動後才在室內顯現。因此診斷會追查屋頂上可能的進水路徑，而不只是直接修補水漬上方。",
      },
      {
        question: "你們在大溫哥華提供當日屋頂漏水維修嗎？",
        answer:
          "Revolin 會在排期和日光條件允許時，儘量當日處理主動漏水，但到場時間不作保證。分享照片和對所見情況的清晰描述，有助於分診溝通。",
      },
      {
        question: "漏水維修會是臨時的還是永久的？",
        answer:
          "這取決於天氣、材料供應和排查結果。若首次上門能安全完成耐久的局部維修，就會這樣做；否則先用臨時防水穩定屋頂，再以書面形式確定永久工程範圍。",
      },
      {
        question: "緊急屋頂漏水維修費用是多少？",
        answer:
          "價格取決於屋頂高度和進場條件、隱蔽損壞範圍，以及除維修外是否還需要臨時保護。Revolin 在評估後提供針對具體項目的報價，而不是未經現場查看就報價。",
      },
    ],
    publicPriceHint:
      "緊急工程範圍取決於進場條件、施工時的天氣、水已蔓延的距離，以及永久維修前是否需要臨時保護。請申請評估以獲取針對具體項目的報價。",
  },
  "flashing-roof-penetration-repair": {
    title: "泛水 / 屋頂穿透點維修",
    shortTitle: "泛水與穿透點",
    eyebrow: "過渡與接口",
    description:
      "維修煙囪、牆體、通風口、管道防水套及其他屋頂穿透點泛水：這些是大溫哥華屋頂常見的進水位置。",
    definition:
      "泛水和屋頂穿透點維修修正屋頂覆蓋層與牆體、煙囪、通風口、管道及其他穿透點之間的導水接口。金屬件、防水套或密封膠接縫失效時，這些細部是頻繁的漏水來源。",
    forWhom:
      "在煙囪、側牆、管道立管、機械通風口或其他屋頂開口附近發現漏水，或泛水老化、已無法正確導水的業主。",
    warningSigns: [
      "煙囪井道或外牆附近出現水漬",
      "穿透點密封膠開裂或硬化",
      "踏步泛水 或 反泛水 翹起、生鏽或分離",
      "橡膠管道防水套老化破裂",
      "牆體與屋頂交接處在風雨天進水",
    ],
    evaluates: [
      "泛水幾何形態和材料狀況",
      "與周邊屋面覆蓋層的兼容性",
      "反泛水 和牆體接口完整性",
      "穿透防水套和套環狀況",
      "密封膠是否在掩蓋失效的金屬細部",
    ],
    causes: [
      "熱脹冷縮使接縫張開",
      "紫外線和天氣使防水套、密封膠老化",
      "踏步或反泛水 整合不當",
      "泛水金屬腐蝕或機械損壞",
      "更換覆蓋層時未正確重做穿透點細部",
    ],
    methodology: [
      "識別失效區域內的每個穿透點和牆體接口",
      "僅在需要時打開以確認水流路徑",
      "以兼容細部更換失效防水套、套環或金屬件",
      "將泛水正確重新整合進現場覆蓋層",
      "避免僅依賴表面填縫作為永久防水策略",
    ],
    materials: [
      "踏步、反泛水和 披水泛水",
      "管道防水套和通風套環",
      "關鍵接口處兼容的墊層和冰壩/防水膜",
      "適合沿海暴露及相鄰材料的金屬",
      "製造商兼容的密封膠僅作次要保護",
    ],
    bcClimate:
      "風雨交加和長溼季給泛水和彈性防水套帶來壓力。在乾燥氣候有效的細部，若金屬整合不正確，在 BC 沿海往往更早失效。",
    whenAppropriate: "當現場覆蓋層仍可正常使用，且漏水由局部泛水或穿透點失效引起時。",
    whenOtherService:
      "若周邊覆蓋層已大範圍磨損，或煙囪、天窗、低坡組件需要不同範圍，可能更適合專項服務。",
    process: [
      "描述漏水位置及周圍可見細部",
      "在失效區域屋頂層面檢查泛水和穿透點",
      "書面維修範圍，標明每個待修正細部",
      "以兼容金屬、防水套和膜材重新細部處理",
      "複核已修正接口及周邊覆蓋層",
    ],
    solutionCategories: [
      {
        title: "穿透防水套更換",
        body: "在橡膠或通風防水套開裂進水前更換老化防水套。",
      },
      {
        title: "牆體與 踏步泛水維修",
        body: "恢復 踏步和反泛水 已無法導水的牆體與屋頂接口。",
      },
      {
        title: "煙囪及複雜接口工程",
        body: "處理需與覆蓋層按正確順序施工的多平面泛水組件。",
      },
    ],
    repairVsReplace: {
      repair: "當屋頂現場完好且失效集中在接口時，泛水維修適用。",
      replace: "若泛水須完全重新整合進已至末期的覆蓋層，可能需要現場更換。",
    },
    scopeFactors: [
      "穿透點數量和類型",
      "牆體飾面接口",
      "金屬材料兼容性",
      "煙囪和老虎窗周圍的進場",
      "現有覆蓋層狀況",
      "是否需要打開相鄰組件",
    ],
    faqs: [
      {
        question: "什麼是泛水 / 屋頂穿透點維修？",
        answer:
          "泛水和屋頂穿透點維修修正屋頂覆蓋層與牆體、煙囪、通風口、管道及其他穿透點之間的導水接口。金屬件、防水套或密封膠接縫失效時，這些細部是頻繁的漏水來源。",
      },
      {
        question: "怎麼知道漏水是泛水問題？",
        answer:
          "在煙囪、側牆、天窗、管道立管或通風口附近出現的漏水，尤其在風雨天，通常與泛水有關。室內水漬與上方穿透點對齊是強烈信號，但仍需屋頂層面檢查確認。",
      },
      {
        question: "不換屋頂能修泛水嗎？",
        answer:
          "通常可以，當週邊覆蓋層仍可使用時。單個泛水、防水套和套環可更換並重新編入現有屋頂。若現場已至末期，重新細部處理更適合與更換規劃一併進行。",
      },
      {
        question: "為什麼給泛水打膠沒能止住漏水？",
        answer:
          "密封膠老化遠快於金屬，無法彌補錯誤的泛水幾何形態。表面填膠常掩蓋失效，而水仍繼續進入。耐久維修須重建金屬細部；密封膠只起次要作用。",
      },
      {
        question: "BC 沿海管道防水套和泛水能用多久？",
        answer:
          "使用壽命因材料、暴露和安裝質量而異，因此沒有單一誠實數字。橡膠管道防水套通常比金屬泛水老化更快，因為紫外線和溫度循環使彈性體硬化，沿海風雨也更早暴露薄弱細部。",
      },
    ],
    publicPriceHint:
      "價格反映穿透點數量和類型、牆體接口複雜程度，以及需打開並重新整合的周邊覆蓋層範圍。請申請評估。",
  },
  "moss-removal-roof-gutter": {
    title: "苔蘚清除＋屋頂／檐溝組合維護",
    shortTitle: "苔蘚與檐溝組合",
    eyebrow: "維護組合",
    description:
      "苔蘚處理、雜物清除、檐溝清潔及屋頂目視檢查，適用於有機生長和排水堵塞加速屋面磨損的大溫哥華住宅。",
    definition:
      "結合苔蘚清除與屋頂／檐溝維護的服務，處理屋面有機生長、清理檐溝和落水管，並記錄可見屋頂狀況以供規劃。",
    forWhom: "屋面或檐溝有苔蘚、地衣或大量雜物，尤其位於樹蔭下的業主。",
    warningSigns: [
      "苔蘚層在瓦片上積留水分",
      "普通降雨時檐溝溢流",
      "朝北坡面有機生長",
      "天溝內雜物淤塞",
      "落水管無法自由排水",
    ],
    evaluates: [
      "苔蘚與有機生長範圍",
      "生長下方覆蓋層狀況",
      "檐溝與落水管水流",
      "天溝和邊緣雜物",
      "是否已有需維修的損壞",
    ],
    causes: [
      "陰涼潮濕的沿海條件",
      "樹木雜物",
      "疏於檐溝維護",
      "表面紋理留住有機物",
      "涼爽月份屋面乾燥不足",
    ],
    methodology: [
      "保護景觀和進場通道",
      "以適合覆蓋層的方法清除苔蘚和雜物",
      "清潔檐溝並沖洗落水管",
      "進行目視狀況複查",
      "記錄清潔過程中發現的維修優先事項",
    ],
    materials: ["適合屋頂類型的軟質清潔工具", "使用時經批准的處理產品", "檐溝清理和沖洗設備"],
    bcClimate:
      "低陸平原的陰影和降雨為苔蘚創造理想條件。生長會在覆蓋層上留住水分，若忽視可加速顆粒脫落。",
    whenAppropriate: "當屋面覆蓋層基本完好，而生長、雜物和排水是主要問題時。",
    whenOtherService: "瓦片缺失、主動漏水或泛水失效需要維修服務，而非僅清潔。",
    process: [
      "提供屋頂類型、遮蔭暴露和進場細節",
      "現場複查生長範圍、排水和覆蓋層狀況",
      "書面界定清潔、處理和檐溝工程範圍",
      "仔細清除、適當處理並沖洗落水管",
      "附照片記錄需維修事項",
    ],
    solutionCategories: [
      {
        title: "苔蘚與有機物清除",
        body: "在適當屋面表面減少積留水分的生長。",
      },
      {
        title: "檐溝與落水管清潔",
        body: "恢復檐口和排放點的自由排水。",
      },
      {
        title: "屋頂目視複查",
        body: "清除雜物後記錄受損覆蓋層。",
      },
    ],
    repairVsReplace: {
      repair: "覆蓋層仍可使用且生長和雜物是主因時，清潔適當。",
      replace: "若清潔後顯示普遍脆化，規劃更換可能比反覆清潔更誠實。",
    },
    scopeFactors: [
      "屋面面積",
      "坡度與進場",
      "樹木覆蓋密度",
      "檐溝長度",
      "覆蓋層脆弱程度",
      "廢料處理",
    ],
    faqs: [
      {
        question: "什麼是苔蘚清除＋屋頂／檐溝組合維護？",
        answer:
          "結合苔蘚清除與屋頂／檐溝維護的服務，處理屋面有機生長、清理檐溝和落水管，並記錄可見屋頂狀況以供規劃。",
      },
      {
        question: "高壓水槍適合清除屋面苔蘚嗎？",
        answer:
          "一般不建議對瀝青瓦使用高壓沖洗，因會剝落保護顆粒並可能將水打入覆蓋層下。苔蘚宜用較溫和的機械方法和適合屋面材料的處理方式清除。",
      },
      {
        question: "苔蘚真的會損壞屋頂，還是只是外觀問題？",
        answer:
          "成熟的苔蘚會在覆蓋層上留住水分、翹起瓦片邊緣並加速顆粒脫落。在雪松上會使木材保持潮濕並加速腐朽。及早清除遠比修復其造成的磨損更經濟。",
      },
      {
        question: "大溫哥華屋頂應多久除苔一次？",
        answer:
          "取決於遮蔭、樹木覆蓋和屋面朝向：朝北和重蔭坡面再生最快。許多本地住宅適合每年檢查，在生長開始成片時清潔，而非固定週期。",
      },
      {
        question: "除苔能解決檐溝溢流嗎？",
        answer:
          "兩者常相關：苔蘚碎片和屋面雜物是檐溝和落水管堵塞的常見原因。此組合同時清理兩者，以便單獨判斷排水。若清理後仍溢流，可能是金屬或坡度問題。",
      },
    ],
    publicPriceHint:
      "價格取決於屋面面積、坡度、生長程度、檐溝長度和進場條件。申請評估以取得專案報價。",
  },
  "asphalt-shingle-replacement": {
    title: "瀝青瓦屋頂更換",
    shortTitle: "瀝青瓦更換",
    eyebrow: "陡坡重鋪",
    description: "為大溫哥華陡坡住宅提供完整的瀝青瓦拆除、墊層、瓦片、泛水整合及廢料清運。",
    definition:
      "瀝青瓦屋頂更換是一次完整的陡坡重鋪：拆除現有瓦面，處理基層和墊層需求，安裝新的瓦片系統，並重新整合泛水和穿透點。",
    forWhom: "瓦片已大範圍磨損、反覆維修，或不再符合住宅剩餘使用計劃的業主。",
    warningSigns: [
      "瓦片捲曲、開裂或大面積顆粒脫落",
      "多個已維修屋面仍反覆失效",
      "瓦片變脆、無法再重新密封",
      "天溝、屋脊和簷口老化",
      "翻修工程使整體重鋪成為更合理的選擇",
    ],
    evaluates: [
      "現有層數和屋面板狀況",
      "坡度、天溝、老虎窗和幾何形態",
      "通風及閣樓受潮跡象",
      "泛水和穿透點清單",
      "進場、保護和廢料清運",
    ],
    causes: [
      "正常使用壽命末期風化",
      "長期苔蘚和潮濕",
      "累積的風損",
      "通風不良加速老化",
      "以往安裝細部薄弱",
    ],
    methodology: [
      "記錄狀況和可選方案",
      "規劃拆除、保護和清運",
      "按發現情況維修屋面板並書面說明",
      "按順序安裝墊層、泛水和現場瓦片",
      "將屋脊、天溝、邊緣和穿透點作為完整組件細部處理",
    ],
    materials: [
      "建築型瀝青瓦",
      "墊層及在適當位置的冰壩/防水膜",
      "天溝、牆體和穿透點泛水",
      "屋脊和通風配件",
      "緊固件和邊緣構件",
    ],
    bcClimate: "沿海降雨和苔蘚生長使墊層、天溝細部和通風在大溫地區瀝青瓦屋頂上尤為重要。",
    whenAppropriate: "當瓦片老化具有系統性，或繼續局部修補仍會留下其他近期失效風險時。",
    whenOtherService: "若周邊系統仍可正常使用，孤立風損可能仍適合局部維修。",
    process: [
      "評估覆蓋層、屋面板指標和通風",
      "書面工程範圍，含材料和細部選項",
      "拆除並做好物業保護和廢料清運",
      "按順序進行屋面板維修、墊層、泛水和瓦片安裝",
      "最終巡查和完工複核",
    ],
    solutionCategories: [
      {
        title: "建築型複合瀝青瓦",
        body: "根據幾何形態、暴露條件和設計意圖選擇的立體住宅瓦型。",
      },
      {
        title: "墊層與排水細部",
        body: "協調現場瓦片與天溝、簷口和過渡部位。",
      },
      {
        title: "泛水重新整合",
        body: "牆體、煙囪、通風口和邊緣作為新組件的一部分進行細部處理。",
      },
    ],
    repairVsReplace: {
      repair: "當損壞侷限且匹配材料能幹淨整合時，局部維修可行。",
      replace: "當磨損廣泛或組件無法接受耐久補丁時，更換更為合理。",
    },
    scopeFactors: [
      "屋頂面積和坡度",
      "層數",
      "屋面板維修",
      "天溝和老虎窗複雜程度",
      "材料選擇",
      "進場和清運",
    ],
    faqs: [
      {
        question: "什麼是瀝青瓦屋頂更換？",
        answer:
          "瀝青瓦屋頂更換是一次完整的陡坡重鋪：拆除現有瓦面，處理基層和墊層需求，安裝新的瓦片系統，並重新整合泛水和穿透點。",
      },
      {
        question: "瀝青瓦屋頂更換需要多長時間？",
        answer:
          "大多數獨立住宅重鋪以天計而非以周計，但工期取決於屋頂大小、坡度、天氣窗口，以及拆除過程中發現的屋面板維修量。書面工程範圍會在開工前說明預期。",
      },
      {
        question: "可以在舊瓦片上直接鋪新瓦嗎？",
        answer:
          "疊鋪有時在技術上可行，但通常更傾向拆除：這樣可以檢查屋面板、正確安裝墊層和泛水，並避免增加重量、把問題困在新覆蓋層下方。",
      },
      {
        question: "拆除時發現腐爛屋面板怎麼辦？",
        answer:
          "發現的屋面板損壞會在新系統安裝前記錄並維修。清晰的工程範圍會事先說明屋面板工作如何計量和定價，使中途發現透明處理，而非意外追加。",
      },
      {
        question: "哪種瓦片最適合大溫哥華天氣？",
        answer:
          "建築型複合瀝青瓦是當地常見的住宅選擇。在此氣候下，墊層質量、天溝和邊緣細部以及閣樓通風，對實際使用壽命的影響往往不亞於瓦片產品本身。",
      },
    ],
    publicPriceHint:
      "項目價格取決於屋頂面積、坡度、層數、屋面板維修、天溝和老虎窗複雜程度及材料選擇。請申請評估以獲取書面工程範圍。",
  },
  "roof-health-check-maintenance": {
    title: "屋頂健康檢查與維護",
    shortTitle: "健康檢查",
    eyebrow: "先看狀況",
    description: "檢查、拍照、少量密封膠或維護工作，以及供規劃參考的屋頂狀況報告。",
    definition:
      "屋頂健康檢查是對覆蓋層、泛水、排水及可接近相關組件的結構化目視評估，通常配合照片記錄和事先約定的有限維護項目。",
    forWhom: "準備買賣、計劃翻修、注意到早期警示跡象，或希望在雨季前建立有記錄基線的業主。",
    warningSigns: [
      "不確定屋頂年齡或剩餘狀況",
      "輕微水漬但來源不明",
      "季節性雜物和苔蘚積聚",
      "穿透點密封膠老化",
      "需要有記錄的計劃基線",
    ],
    evaluates: [
      "按屋面劃分的覆蓋層狀況",
      "泛水和穿透點",
      "排水和天溝接口",
      "可見通風組件",
      "維護與維修事項的優先排序",
    ],
    causes: [
      "正常老化但缺乏記錄",
      "維護拖延",
      "場地特定的天氣暴露",
      "質量不明的以往工程",
      "買賣或翻修決策需求",
    ],
    methodology: [
      "瞭解關注點和可獲取的歷史",
      "系統檢查外部屋頂區域",
      "拍攝代表性狀況照片",
      "僅完成約定的少量維護",
      "提供清晰的狀況摘要和選項",
    ],
    materials: [
      "照片記錄",
      "包含時的少量密封膠或緊固件",
      "若納入範圍則清理有限雜物",
      "書面狀況說明",
    ],
    bcClimate: "季節性降雨使入冬前和暴風雨後的健康檢查有助於發現堵塞排水、材料翹起和早期泛水失效。",
    whenAppropriate: "在承諾維修、更換或進一步調查前需要清晰瞭解狀況時。",
    whenOtherService: "主動嚴重漏水或暴風雨開口應走緊急或維修路徑。",
    process: [
      "預約健康檢查並說明任何關注點",
      "系統性外部檢查並拍照記錄",
      "上門期間完成約定的少量維護",
      "書面狀況摘要及排序後的優先事項",
      "就建議的維修進行後續討論",
    ],
    solutionCategories: [
      {
        title: "有記錄的檢查",
        body: "對屋頂組件上可見且可接近部分的結構化審閱。",
      },
      {
        title: "少量維護",
        body: "僅在納入範圍且適當時進行的有限密封膠或雜物工作。",
      },
      {
        title: "規劃摘要",
        body: "區分需立即處理的事項與中期監測事項。",
      },
    ],
    repairVsReplace: {
      repair: "許多發現是維護或局部維修事項，而非全面更換的觸發因素。",
      replace: "僅當觀察到的狀況支持更廣泛路徑時才討論更換。",
    },
    scopeFactors: [
      "屋頂大小和複雜程度",
      "進場條件",
      "照片記錄深度",
      "包含的維護項目",
      "物業上的屋頂系統數量",
    ],
    faqs: [
      {
        question: "什麼是屋頂健康檢查與維護？",
        answer:
          "屋頂健康檢查是對覆蓋層、泛水、排水及可接近相關組件的結構化目視評估，通常配合照片記錄和事先約定的有限維護項目。",
      },
      {
        question: "我應該多久檢查一次屋頂？",
        answer:
          "當地較有用的節奏是秋季降雨前檢查一次，並在重大風暴後再查。樹木覆蓋重的住宅，或屋頂已進入預期壽命後半段的住宅，值得更頻繁關注。",
      },
      {
        question: "健康檢查後我會收到什麼？",
        answer:
          "附有照片的覆蓋層、泛水、排水和通風狀況摘要，區分需及時維修的事項、需監測的事項和正常磨損。這是一份可按自己節奏採取行動的規劃文件。",
      },
      {
        question: "買賣房屋前做屋頂檢查值得嗎？",
        answer:
          "值得：屋頂是較昂貴的建築組件之一，有記錄的狀況基線能減少談判中的猜測。請注意這是屋頂狀況審閱，而非正式的購房驗房。",
      },
      {
        question: "檢查時發現的問題會當場修嗎？",
        answer:
          "事先約定的小維護，例如穿透點少量密封膠更新，可在上門時完成。較大發現會記錄並單獨確定範圍，讓您憑清晰信息做決定，而非現場施壓。",
      },
    ],
    publicPriceHint:
      "健康檢查範圍取決於屋頂大小和複雜程度、進場條件，以及包含多少記錄和少量維護。請申請評估。",
  },
  "shingle-spot-repair": {
    title: "瀝青瓦局部維修",
    shortTitle: "瓦片局部維修",
    eyebrow: "局部覆蓋層維修",
    description: "在周邊屋面仍可使用時，對缺失、風損、開裂或其他損壞的瀝青瓦進行局部維修。",
    definition:
      "瀝青瓦局部維修在匹配和周邊條件允許時，更換或重新固定有限區域的損壞瀝青瓦，無需整體重鋪。",
    forWhom: "陡坡屋頂整體仍完好、僅出現孤立瓦片損壞的業主。",
    warningSigns: [
      "風後缺失瓦片",
      "瓦片開裂",
      "局部區域外露釘件或墊層",
      "某區域顆粒堆積",
      "先前修補翹起",
    ],
    evaluates: [
      "瓦片可匹配性",
      "周邊屋面柔韌度",
      "局部墊層狀況",
      "受損坡面暴露程度",
      "損壞是否真正孤立",
    ],
    causes: ["風掀", "撞擊損壞", "熱裂", "踩踏損壞", "密封條老化失效"],
    methodology: [
      "確認可匹配性",
      "小心拆除損壞瓦片",
      "需要時局部更換墊層",
      "以正確固定方式安裝匹配瓦片",
      "僅在細部要求處使用密封",
    ],
    materials: ["匹配的瀝青瓦", "局部墊層", "兼容緊固件", "僅在指定細部使用的密封膠"],
    bcClimate: "沿海風害和濕冷冬季使及時局部維修很重要，以免水滲入屋面板。",
    whenAppropriate: "當損壞局限且屋面可接受兼容瓦片時。",
    whenOtherService: "普遍脆化或多坡面失效時，應考慮完整瀝青瓦更換。",
    process: [
      "若可安全看見，請發送受損區域照片",
      "屋頂上確認損壞確實孤立",
      "核對瓦型與顏色匹配",
      "小心拆除並更換受影響瓦片",
      "複查維修區及周邊屋面",
    ],
    solutionCategories: [
      {
        title: "局部瓦片更換",
        body: "以兼容材料恢復局限損壞區。",
      },
      {
        title: "邊緣與屋脊修補",
        body: "在仍屬孤立時修正高應力邊緣失效。",
      },
      {
        title: "漏水路徑核查",
        body: "確認局部損壞未掩蓋泛水失效。",
      },
    ],
    repairVsReplace: {
      repair: "屋面仍柔韌且可匹配時適合局部維修。",
      replace: "磨損具系統性時應整體更換。",
    },
    scopeFactors: ["損壞面積", "坡度與進場", "材料匹配", "墊層需求", "天氣窗口"],
    faqs: [
      {
        question: "什麼是瀝青瓦局部維修？",
        answer:
          "瀝青瓦局部維修在匹配和周邊條件允許時，更換或重新固定有限區域的損壞瀝青瓦，無需整體重鋪。",
      },
      {
        question: "暴風雨吹掉幾片瓦，緊急嗎？",
        answer:
          "應盡快處理。缺失瓦片會使墊層暴露，而墊層並非為長期風雨設計，下一場大雨可能滲入屋面板。快速局部維修通常可避免更大問題。",
      },
      {
        question: "替換瓦片能與現有屋頂完全同色嗎？",
        answer:
          "新瓦片很少能與風化瓦片完美匹配，因現有瓦片會褪色並失去顆粒。選擇顏色相近的產品可減少對比，施工前會誠實說明匹配預期。",
      },
      {
        question: "多少片損壞算太多、不適合局部維修？",
        answer:
          "沒有固定數量：關鍵是損壞是否局限，以及周邊屋面是否仍足夠柔韌以接受施工而不開裂。多坡面反覆失效通常意味著更換規劃更經濟。",
      },
      {
        question: "可以只封住開裂瓦片而不更換嗎？",
        answer:
          "在開裂瓦片上表面填縫壽命短，且可能使日後維修更困難。更換損壞瓦片可恢復設計的排水搭接，通常是更耐久、長期更省的做法。",
      },
    ],
    publicPriceHint:
      "局部維修價格取決於損壞面積、坡度與進場，以及替換瓦片的匹配程度。申請評估以取得專案報價。",
  },
  "gutter-cleaning-downspout-flush": {
    title: "檐溝清潔＋落水管沖洗",
    shortTitle: "檐溝清潔",
    eyebrow: "排水維護",
    description: "清潔檐溝並沖洗落水管，使檐口排水在大溫哥華降雨時正常運作。",
    definition:
      "檐溝清潔清除檐溝和落水管中的雜物，使屋面徑流能離開檐口線，而不溢入封檐板、地基或景觀。",
    forWhom: "普通降雨時檐溝溢流、槽內長草，或普通雨後封檐板出現水漬的業主。",
    warningSigns: [
      "水沿檐溝外壁流下",
      "槽內長植物",
      "地基濺水",
      "封檐板水漬",
      "測試時落水管不排水",
    ],
    evaluates: ["雜物量", "槽體坡度", "出水口和延伸管狀況", "封檐板固定", "金屬是否已失效"],
    causes: ["樹葉和苔蘚雜物", "針葉樹針葉堆積", "坡度下沉", "防護網堵塞", "錯過季節性清潔"],
    methodology: [
      "清理槽體",
      "沖洗落水管",
      "檢查出水口和延伸管",
      "記錄金屬狀況",
      "僅在有依據時建議防護網或更換",
    ],
    materials: ["清潔工具", "沖洗設備", "僅在範圍內的基本緊固件"],
    bcClimate: "頻繁降雨意味著堵塞的檐溝會很快浸濕封檐板和地基。",
    whenAppropriate: "當金屬仍完好且雜物是主要問題時。",
    whenOtherService: "生鏽、分離或長期規格不足的檐溝可能需要更換。",
    process: [
      "提供房屋高度、檐溝長度和進場說明",
      "清除樹葉、針葉和顆粒雜物",
      "沖洗每條落水管並確認自由排放",
      "作業時記錄金屬、坡度和封檐板狀況",
      "報告清潔無法單獨解決的問題",
    ],
    solutionCategories: [
      {
        title: "槽體清潔",
        body: "清除檐溝中有機物和顆粒雜物。",
      },
      {
        title: "落水管沖洗",
        body: "恢復連續排放路徑。",
      },
      {
        title: "狀況記錄",
        body: "清潔時標記失效金屬或封檐板問題。",
      },
    ],
    repairVsReplace: {
      repair: "槽體結構仍完好時清潔即可。",
      replace: "清潔後金屬或坡度仍無法排水時需更換。",
    },
    scopeFactors: ["線性長度", "層數與進場", "雜物類型", "落水管數量", "安全要求"],
    faqs: [
      {
        question: "什麼是檐溝清潔＋落水管沖洗？",
        answer:
          "檐溝清潔清除檐溝和落水管中的雜物，使屋面徑流能離開檐口線，而不溢入封檐板、地基或景觀。",
      },
      {
        question: "大溫哥華檐溝應多久清潔一次？",
        answer:
          "大多數本地住宅至少需要秋季落葉後清潔一次；針葉樹下的物業常需第二次，因針葉全年累積。普通降雨時溢流表示已等太久。",
      },
      {
        question: "跳過檐溝清潔會怎樣？",
        answer:
          "堵塞的檐溝會對封檐板和外牆溢流、向地基排水，並可能使水倒灌屋頂邊緣。在此氣候下，小維護任務常演變成封檐板腐朽或排水維修。",
      },
      {
        question: "會沖洗落水管還是只清槽體？",
        answer:
          "兩者都做。乾淨槽體若接入堵塞落水管仍會溢流。每條落水管都會沖洗並確認排放，因下彎和地下堵塞是最常見的隱蔽失效。",
      },
      {
        question: "清潔後檐溝仍溢流，怎麼辦？",
        answer:
          "這指向坡度、尺寸或出水口問題，而非雜物。清潔上門會附狀況記錄；若金屬或幾何形態無法排水，會建議維修或更換並記錄原因。",
      },
    ],
    publicPriceHint:
      "價格通常取決於線性長度、層數、雜物負荷以及安全與進場要求。申請評估以取得專案報價。",
  },
  "storm-tarping-emergency-dry-in": {
    title: "暴風雨鋪布／緊急防雨封蓋",
    shortTitle: "暴風雨防雨",
    eyebrow: "臨時防水",
    description: "風災、樹木撞擊或暴風雨損壞後的臨時防水，直至可進行永久屋頂工程。",
    definition:
      "暴風雨鋪布和緊急防雨封蓋暫時保護開口或受損屋面免受天氣影響，在規劃永久維修期間限制室內損壞。",
    forWhom: "屋面板外露、樹木撞擊，或暴風雨後有大面積缺失的業主。",
    warningSigns: [
      "樹木撞擊後屋面板外露",
      "大面積屋面缺失",
      "新開口處室內進雨",
      "屋頂有不穩定碎屑",
      "永久材料施工前需臨時保護",
    ],
    evaluates: ["安全與進場", "開口大小與位置", "室內風險", "天氣窗口", "通往永久維修的路徑"],
    causes: ["風暴", "樹木倒塌", "覆蓋層突然缺失", "撞擊損壞", "老化材料部分失效"],
    methodology: [
      "優先安全評估",
      "僅清除安全防雨所需雜物",
      "安裝臨時防水",
      "記錄狀況",
      "規劃永久維修路徑",
    ],
    materials: ["防水布和臨時膜材", "適合臨時工程的牢固固定", "開口和邊緣保護"],
    bcClimate: "太平洋風暴可迅速打開屋面；臨時防雨封蓋在界定永久範圍前限制二次室內損壞。",
    whenAppropriate: "開口或不穩定區域在永久工程前必須防雨時。",
    whenOtherService: "防雨封蓋不能替代受損組件的永久維修或更換。",
    process: [
      "聯繫並提供照片及安全細節（如有）",
      "現場危險與進場評估",
      "僅為安全覆蓋穩定必要碎屑",
      "在開口上固定臨時防水",
      "記錄狀況並規劃永久維修",
    ],
    solutionCategories: [
      {
        title: "臨時防水",
        body: "限制暴風雨開口的進水。",
      },
      {
        title: "穩定處理",
        body: "固定可能加劇損壞的鬆動材料。",
      },
      {
        title: "狀況記錄",
        body: "記錄狀況以支持永久維修規劃。",
      },
    ],
    repairVsReplace: {
      repair: "無法立即完成永久工程時，先臨時防雨封蓋。",
      replace: "組件可正確恢復後進行永久維修或更換。",
    },
    scopeFactors: ["開口面積", "進場與高度", "天氣", "室內暴露", "碎屑危險"],
    faqs: [
      {
        question: "什麼是暴風雨鋪布／緊急防雨封蓋？",
        answer:
          "暴風雨鋪布和緊急防雨封蓋暫時保護開口或受損屋面免受天氣影響，在規劃永久維修期間限制室內損壞。",
      },
      {
        question: "樹木砸中屋頂，第一步該做什麼？",
        answer:
          "讓人遠離受損下方房間，不要上屋頂，從地面和室內拍照。然後安排專業防雨封蓋：不穩定樹枝和隱蔽結構損壞使暴風雨受損屋面很危險。",
      },
      {
        question: "屋頂防水布能留多久？",
        answer:
          "防水布是臨時天氣保護，不是屋面。實際壽命以週至數月計，取決於暴露、固定和冬季風暴。材料和條件允許時應盡快安排永久維修。",
      },
      {
        question: "保險會賠緊急鋪布嗎？",
        answer:
          "許多財產保單將突發損壞後合理的臨時保護視為可賠付的減損措施，但保障因保單而異。Revolin 會記錄狀況和已完成工作以支持理賠，但無法就您的保單條款提供建議。",
      },
      {
        question: "防雨封蓋能代替修屋頂嗎？",
        answer:
          "不能。防雨封蓋在規劃正確維修或更換期間限制室內損壞。長期留置臨時保護通常會因固定件和材料老化而導致持續進水。",
      },
    ],
    publicPriceHint:
      "防雨封蓋範圍取決於開口大小、屋頂高度、施工期間天氣及碎屑危險。申請評估以取得專案報價。",
  },
  "skylight-leak-flashing-repair": {
    title: "天窗漏水／泛水維修",
    shortTitle: "天窗維修",
    eyebrow: "天窗接口",
    description: "維修或重做漏水天窗及其周邊屋面的泛水。",
    definition: "天窗漏水與泛水維修處理天窗基座、泛水及相鄰屋面的進水，而非假設僅是玻璃本身問題。",
    forWhom: "天窗下方有水漬、基座泛水失效，或不確定是冷凝還是真漏水的業主。",
    warningSigns: [
      "天窗井下方水漬",
      "光井油漆剝落",
      "基座角部可見 daylight",
      "單元旁踏步泛水失效",
      "低坡屋頂基座旁積水",
    ],
    evaluates: [
      "天窗類型與年齡",
      "基座與泛水狀況",
      "相鄰覆蓋層整合",
      "室內冷凝模式",
      "是否需要更換單元",
    ],
    causes: [
      "基座泛水失效",
      "與瓦片或膜材整合不當",
      "密封老化",
      "冷凝被誤判為漏水",
      "框架開口位移",
    ],
    methodology: [
      "盡可能區分漏水與冷凝",
      "檢查基座泛水及相鄰屋面",
      "以兼容細部重做泛水或維修",
      "按需處理周邊覆蓋層",
      "僅在有依據時建議更換單元",
    ],
    materials: ["基座泛水", "兼容墊層或膜材細部", "周邊瓦片或膜材補片", "密封膠僅作次要措施"],
    bcClimate: "風雨會迅速找到沿海卑詩省薄弱的天窗基座；僅看玻璃狀況很少能解釋所有漏水。",
    whenAppropriate: "當天窗單元仍可使用且失效在泛水或相鄰屋面時。",
    whenOtherService: "框架失效、長期漏水的密封單元或更廣泛屋頂失效可能需要不同範圍。",
    process: [
      "描述水漬模式及已知天窗年齡",
      "檢查基座、泛水和相鄰屋面",
      "區分漏水證據與冷凝",
      "重做泛水或維修失效細部",
      "複查接口並記錄工作",
    ],
    solutionCategories: [
      {
        title: "基座重做泛水",
        body: "恢復天窗基座處的金屬和膜材接口。",
      },
      {
        title: "相鄰覆蓋層維修",
        body: "修正已無法與單元整合的瓦片或膜材。",
      },
      {
        title: "漏水與冷凝判別",
        body: "協助區分組件漏水與室內潮濕症狀。",
      },
    ],
    repairVsReplace: {
      repair: "單元和基座可接受耐久重做細部時進行維修。",
      replace: "組件無法可靠防水時更換單元或屋頂。",
    },
    scopeFactors: ["天窗數量與尺寸", "屋頂系統類型", "基座高度與狀況", "進場", "室內裝修風險"],
    faqs: [
      {
        question: "什麼是天窗漏水／泛水維修？",
        answer: "天窗漏水與泛水維修處理天窗基座、泛水及相鄰屋面的進水，而非假設僅是玻璃本身問題。",
      },
      {
        question: "天窗是漏水還是冷凝？",
        answer:
          "冷凝表現為玻璃和框架上的細小水珠或霧氣，常見於寒冷天氣和潮濕房間。漏水從基座角部或上緣蔓延，常染色光井石膏板。屋頂層面診斷可確認。",
      },
      {
        question: "漏水的天窗一定要換嗎？",
        answer:
          "不一定。許多天窗漏水源於泛水或周邊屋面，而非單元本身。若中空玻璃密封失效，層間持續起霧，或框架腐朽，則討論更換；否則重做泛水通常可解決。",
      },
      {
        question: "重鋪屋頂時應一併換天窗嗎？",
        answer:
          "常是實際時機：泛水無論如何都要重做，進場已在位，老化單元不太可能比周邊新屋頂更耐用。狀況良好的單元可保留並正確重做泛水。",
      },
      {
        question: "為什麼只在風雨時漏？",
        answer:
          "風推雨水對泛水細部的載荷與垂直落雨不同：可能沿搭接上升並進入垂直排水良好的細部。此模式強烈指向泛水幾何問題，而非玻璃密封失效。",
      },
    ],
    publicPriceHint:
      "天窗工程按單元情境計價：基座狀況、屋頂系統類型、泛水要求和進場。申請評估以取得專案報價。",
  },
  "chimney-flashing-repair": {
    title: "煙囪泛水維修",
    shortTitle: "煙囪泛水",
    eyebrow: "煙囪接口",
    description: "大溫哥華屋頂上踏步泛水、反泛水及煙囪相關漏水的修正。",
    definition: "煙囪泛水維修恢復磚砌或框架煙囪與屋面交接處排水的金屬接口。",
    forWhom: "煙囪角部漏水、反泛水生鏽，或分水脊細部老化的業主。",
    warningSigns: [
      "煙囪角部水漬",
      "反泛水鏽痕",
      "磚體問題伴屋頂下方漏水",
      "寬煙囪缺少分水脊",
      "煙囪底部塗過瀝青",
    ],
    evaluates: [
      "煙囪尺寸與幾何",
      "踏步與反泛水狀況",
      "上游分水脊需求",
      "磚體接口",
      "周邊覆蓋層狀況",
    ],
    causes: ["踏步泛水編入不當", "反泛水與磚體分離", "熱位移", "缺少上游分水脊", "老化與腐蝕"],
    methodology: [
      "檢查煙囪各面與屋面交接",
      "僅在需要時打開以重建金屬層次",
      "正確安裝踏步與反泛水",
      "需要時增設或恢復分水脊",
      "重新整合周邊覆蓋層",
    ],
    materials: [
      "踏步與反泛水",
      "需要時的分水脊構架/覆蓋材料",
      "兼容墊層",
      "僅作適當次要細部的砂漿或密封膠",
    ],
    bcClimate: "煙囪角部是低陸平原風雨中的高應力潮濕區；表面瀝青不能替代持久的金屬層次。",
    whenAppropriate: "當煙囪及周邊屋面主區域可支持正確重做泛水時。",
    whenOtherService: "若主區域已接近壽命末期，重做泛水宜與更換規劃一併進行。",
    process: [
      "分享煙囪及室內水漬照片",
      "檢查煙囪各面與屋面交接",
      "書面界定待重建的金屬層次",
      "正確安裝踏步、反泛水和分水脊",
      "重新整合覆蓋層並複查結果",
    ],
    solutionCategories: [
      {
        title: "踏步泛水恢復",
        body: "與屋面覆蓋層重建編入的金屬層次。",
      },
      {
        title: "反泛水修正",
        body: "恢復嵌入磚體或飾面的上部金屬收口。",
      },
      {
        title: "分水脊與導水細部",
        body: "寬煙囪需要時改善上游導水。",
      },
    ],
    repairVsReplace: {
      repair: "屋面主區域仍可使用時重做煙囪泛水。",
      replace: "主區域無法支持耐久整合時與重鋪一併進行。",
    },
    scopeFactors: ["煙囪寬度", "屋頂坡度", "磚體狀況", "進場", "周邊覆蓋層年齡"],
    faqs: [
      {
        question: "什麼是煙囪泛水維修？",
        answer: "煙囪泛水維修恢復磚砌或框架煙囪與屋面交接處排水的金屬接口。",
      },
      {
        question: "屋頂是新的為什麼煙囪還漏？",
        answer:
          "煙囪泛水是重鋪後常見薄弱點：若反泛水被重用或踏步泛水未正確重編，新覆蓋層會將水導入舊失效處。煙囪接口須作為獨立組件細部處理。",
      },
      {
        question: "什麼是反泛水，為什麼重要？",
        answer:
          "踏步泛水將水導過瓦片；反泛水是嵌入磚體、覆蓋踏步泛水豎向边的上部金屬層。若反泛水收口失效，沿煙囪面流下的水會直接進入踏步金屬後方。",
      },
      {
        question: "煙囪後面需要分水脊嗎？",
        answer:
          "下游側寬煙囪會在上游面积積水和雜物。分水脊：小型導水屋脊，將水導繞煙囪。是否需要取決於寬度、坡度和觀察狀況。",
      },
      {
        question: "屋面瀝青能當煙囪泛水修復嗎？",
        answer:
          "緊急止漏時瀝青可減緩漏水，但作為維修會失效：收縮、開裂並困住水，同時掩蓋下方金屬失效。持久修正須重建金屬層次。",
      },
    ],
    publicPriceHint:
      "煙囪工程按煙囪寬度、坡度、磚體狀況及需掀起並重新整合的周邊覆蓋層計價。申請評估以取得專案報價。",
  },
  "gutter-replacement": {
    title: "檐溝更換",
    shortTitle: "檐溝更換",
    eyebrow: "檐口排水系統",
    description: "無縫鋁檐溝和落水管更換，確保檐口排水可靠。",
    definition: "檐溝更換安裝尺寸和坡度合適的新檐溝和落水管，將屋面徑流導離建築外殼。",
    forWhom: "清潔後仍反覆溢流、檐溝生鏽分離，或排水失效導致封檐板損壞的業主。",
    warningSigns: [
      "接縫開裂",
      "檐溝脫離封檐板",
      "清潔後仍反覆溢流",
      "腐蝕穿孔",
      "坡度錯誤無法現場修正",
    ],
    evaluates: ["線性長度與層數", "出水口設計", "封檐板狀況", "顏色與型材", "落水管路由"],
    causes: ["金屬壽命末期", "槽體規格不足", "封檐板位移", "雜物負荷", "原始安裝不當"],
    methodology: [
      "確認封檐板就緒",
      "規劃坡度與出水口",
      "安裝無縫槽體和落水管",
      "測試水流路徑",
      "處理濺水與排放點",
    ],
    materials: ["無縫鋁檐溝", "落水管和彎管", "吊架和緊固件", "端蓋和出水口"],
    bcClimate: "高降雨量使正確的檐溝尺寸和坡度對低陸平原住宅至關重要。",
    whenAppropriate: "現有檐溝清潔和小修後仍無法正常排水時。",
    whenOtherService: "若槽體仍完好，清潔或防護網可能已足夠。",
    process: [
      "測量槽體、出水口和落水管路由",
      "確認封檐板可支承新吊架",
      "書面範圍含型材和顏色選擇",
      "現場成型並安裝無縫槽體",
      "水流測試與排放複查",
    ],
    solutionCategories: [
      {
        title: "無縫檐口系統",
        body: "坡度導向受控出水口的新槽體。",
      },
      {
        title: "落水管設計",
        body: "將排放導離地基和人行道。",
      },
      {
        title: "封檐板協調",
        body: "需要時在掛新槽體前處理軟封檐板。",
      },
    ],
    repairVsReplace: {
      repair: "金屬或幾何形態失效時更換。",
      replace: "系統仍完好時僅清潔或維修。",
    },
    scopeFactors: ["線性長度", "高度/進場", "出水口數量", "顏色選擇", "封檐板維修"],
    faqs: [
      {
        question: "什麼是檐溝更換？",
        answer: "檐溝更換安裝尺寸和坡度合適的新檐溝和落水管，將屋面徑流導離建築外殼。",
      },
      {
        question: "無縫檐溝是什麼意思？",
        answer:
          "無縫檐溝在現場按每段槽體連續成型，接縫僅在轉角和出水口。比每隔幾英尺就有接頭的分段檐溝更少未來漏點。",
      },
      {
        question: "換屋頂時應一併換檐溝嗎？",
        answer:
          "若兩者都接近壽命末期，一併施工可避免拆除時損壞新檐溝，並協調滴水邊緣與槽體位置。完好的檐溝不必因換屋頂而更換。",
      },
      {
        question: "大溫哥華住宅需要多大檐溝？",
        answer:
          "尺寸取決於排入每段槽體的屋面面積、坡度和出水口佈局，而非統一答案。本地降雨強度會迅速暴露規格不足或排水不足的槽體，因此按住宅評估佈局。",
      },
      {
        question: "如何讓落水管水遠離地基？",
        answer:
          "排放規劃朝向適當排水：延伸管、濺水管理或現有地面排水，使屋面水不直接落在地下室旁。場地坡度和物業排水安排決定正確做法。",
      },
    ],
    publicPriceHint:
      "更換價格取決於線性長度、層數與進場、出水口數量及任何前置封檐板維修。申請評估以取得專案報價。",
  },
  "gutter-guard-installation": {
    title: "檐溝防護網安裝",
    shortTitle: "檐溝防護網",
    eyebrow: "雜物防護",
    description: "減少樹木密集物業上葉片、苔蘚和雜物堵塞頻率的防護系統。",
    definition: "檐溝防護網安裝在檐溝上方的防護系統，減少樹葉和雜物積累，同時仍允許水進入槽體。",
    forWhom: "需頻繁清潔、多層進場困難，或樹木覆蓋重的業主。",
    warningSigns: [
      "每年需清潔多次",
      "多層進場困難",
      "針葉樹覆蓋重",
      "清潔後落水管很快又堵",
      "維護進場受限",
    ],
    evaluates: ["先確認檐溝結構狀況", "雜物類型", "產品適用性", "屋頂邊緣幾何", "維護預期"],
    causes: ["密集綠化", "屋面幾何將雜物導入槽體", "細碎雜物突破簡單網罩", "維護間隔不一致"],
    methodology: [
      "確認檐溝完好",
      "選擇合適防護系統",
      "注意進水與邊緣細部安裝",
      "測試水流",
      "說明殘餘維護需求",
    ],
    materials: ["防護網組件", "與檐溝兼容的緊固件", "所需邊緣配件"],
    bcClimate:
      "沿海樹木會掉落針葉和苔蘚碎片，使不頻繁清潔的計劃失效；防護網可減少但無法消除所有維護。",
    whenAppropriate: "檐溝結構完好且雜物負荷是主要問題時。",
    whenOtherService: "失效檐溝應先更換再裝防護網。",
    process: [
      "評估檐溝狀況及物業雜物類型",
      "推薦適合周邊樹木的防護網類型",
      "書面範圍含任何前置清潔",
      "安裝時注意進水與邊緣細部",
      "誠實說明剩餘維護節奏",
    ],
    solutionCategories: [
      {
        title: "雜物防護系統",
        body: "減少樹葉和針葉進入槽體。",
      },
      {
        title: "兼容性檢查",
        body: "僅在檐溝仍能正常排水時安裝。",
      },
      {
        title: "維護預期",
        body: "說明防護網能減少什麼、什麼仍需定期關注。",
      },
    ],
    repairVsReplace: {
      repair: "在完好檐溝上、雜物負荷高時安裝防護網。",
      replace: "金屬失效時先更換檐溝。",
    },
    scopeFactors: ["線性長度", "產品類型", "屋面雜物狀況", "進場", "現有檐溝狀況"],
    faqs: [
      {
        question: "什麼是檐溝防護網安裝？",
        answer: "檐溝防護網安裝在檐溝上方的防護系統，減少樹葉和雜物積累，同時仍允許水進入槽體。",
      },
      {
        question: "裝了防護網就不用清檐溝了嗎？",
        answer:
          "不是。防護網可大幅減少清潔頻率，但無法消除維護：雜物會在網上積累，天溝仍會集中材料，出水口應定期檢查。誠實說法是更少清潔，而非零維護。",
      },
      {
        question: "防護網對松針有效嗎？",
        answer:
          "細針會突破簡單穿孔網罩。微孔網在針葉樹下表現更好，但細碎雜物膜仍可能需偶爾刷除。產品選擇從屋頂實際掉落物開始。",
      },
      {
        question: "能在現有檐溝上裝嗎？",
        answer:
          "可以，若檐溝結構完好、坡度正確且固定牢固。在失效檐溝上裝防護網會鎖定底層問題，因此先核對狀況：有時需清潔和小調整。",
      },
      {
        question: "大雨時防護網會有問題嗎？",
        answer:
          "正確選型並安裝的系統可通過本地雨量。匹配不當的防護網在強降雨時可能導致檐口溢流，尤其陡坡屋面下方，因此選型會考慮坡度和檐口水速。",
      },
    ],
    publicPriceHint:
      "防護網價格取決於產品類型、線性長度、屋頂邊緣幾何及現有檐溝狀況。申請評估以取得專案報價。",
  },
  "roof-ventilation-upgrade": {
    title: "屋頂通風升級",
    shortTitle: "通風升級",
    eyebrow: "閣樓氣流",
    description: "箱式通風口、屋脊通風、檐口進氣及閣樓氣流修正，支持屋頂與閣樓性能。",
    definition: "屋頂通風升級改善進排氣路徑，使閣樓在現有平衡不足時更有效地管理潮濕和熱量。",
    forWhom: "閣樓冷凝、瓦片老化不均、檐口被堵或排氣不足的業主。",
    warningSigns: [
      "閣樓冷凝或結霜",
      "閣樓霉味",
      "瓦片老化不均",
      "檐口進氣被堵或油漆覆蓋",
      "閣樓過熱加劇潮濕問題",
    ],
    evaluates: ["閣樓配置", "現有排氣口", "進氣路徑連續性", "檐口保溫位置", "屋頂幾何限制"],
    causes: ["排氣不足", "進氣堵塞", "保溫覆蓋檐口", "浴室/廚房風管排入閣樓", "重鋪時未設計通風"],
    methodology: [
      "評估進排氣平衡",
      "可行時清理或恢復檐口進氣",
      "增設或修正排氣構件",
      "避免無進氣的過度排氣",
      "需要時協調保溫和氣密討論",
    ],
    materials: [
      "按規格的屋脊、箱式或其他排氣口",
      "檐口進氣構件",
      "擋板或相關閣樓配件",
      "新穿透點泛水",
    ],
    bcClimate: "涼爽潮濕的卑詩天氣使閣樓潮濕管理重要；通風是系統一部分，不能替代漏水維修。",
    whenAppropriate: "當氣流不平衡導致閣樓潮濕或覆蓋層加速老化時。",
    whenOtherService: "通風升級不能替代漏水維修，也不能解決所有冷凝來源。",
    process: [
      "複查閣樓及外部通風構件",
      "按閣樓容積評估進排氣平衡",
      "書面範圍含通風口、進氣恢復和擋板",
      "安裝並泛水新構件",
      "確認氣流路徑暢通",
    ],
    solutionCategories: [
      {
        title: "排氣升級",
        body: "按需增設或修正屋脊、箱式或其他排氣口。",
      },
      {
        title: "進氣恢復",
        body: "在堵塞處重新開啟或改善檐口進氣路徑。",
      },
      {
        title: "平衡複查",
        body: "為閣樓容積制定連貫的進排氣策略。",
      },
    ],
    repairVsReplace: {
      repair: "有記錄的不平衡時升級通風。",
      replace: "勿將通風口當作防水維修的替代。",
    },
    scopeFactors: ["閣樓容積", "現有通風口類型", "進氣可用性", "屋面覆蓋層類型", "進場"],
    faqs: [
      {
        question: "什麼是屋頂通風升級？",
        answer: "屋頂通風升級改善進排氣路徑，使閣樓在現有平衡不足時更有效地管理潮濕和熱量。",
      },
      {
        question: "閣樓通風不良有什麼跡象？",
        answer:
          "冬季屋面板 underside 冷凝或結霜、閣樓霉味、黴斑、釘尖生鏽、坡面瓦片老化不均。在沿海卑詩省，主導症狀往往是潮濕而非夏季高溫。",
      },
      {
        question: "多加通風口能解決閣樓冷凝嗎？",
        answer:
          "單獨加排氣往往不夠。無匹配檐口進氣的排氣可能從室內而非室外抽氣，有時使潮濕更糟。排入閣樓的浴室風管和居住空間漏氣常是真正驅動因素，因此會評估整條路徑。",
      },
      {
        question: "屋脊通風口和箱式通風口有什麼區別？",
        answer:
          "屋脊通風口沿屋脊連續運行，均勻排氣；箱式通風口是屋脊附近的離散單元。哪種適合取決於屋脊長度、幾何形態及進氣如何分佈。",
      },
      {
        question: "更好通風能讓瓦片更耐用嗎？",
        answer:
          "平衡通風可減少加速瓦片老化並可能損及屋面板的閣樓熱量和潮濕負荷。這是若干因素之一：暴露、材料品質和安裝仍重要，因此通風支持壽命，而非保證壽命。",
      },
    ],
    publicPriceHint:
      "通風工程按閣樓容積、現有通風口類型、進氣可用性及涉及屋面覆蓋層界定。申請評估以取得專案報價。",
  },
  "fascia-soffit-repair-replacement": {
    title: "封檐板＋檐口底板維修／更換",
    shortTitle: "封檐板與檐口",
    eyebrow: "檐口構件",
    description: "維修或更換腐朽或損壞的封檐板、檐口底板和支撐檐溝的檐口構件。",
    definition: "封檐板與檐口底板維修或更換恢復完成屋頂邊緣、支撐檐溝並有助於進氣通風的檐口組件。",
    forWhom: "封檐板變軟、檐口底板下垂、檐溝脫落或檐口有害蟲通道的業主。",
    warningSigns: [
      "封檐板變軟或腐朽",
      "檐口底板下垂",
      "檐溝支架脫落",
      "基材腐朽伴油漆失效",
      "害蟲利用開口進入",
    ],
    evaluates: ["損壞線性範圍", "材料選擇", "檐口通風需求", "檐溝重新固定計劃", "油漆或飾面範圍"],
    causes: ["檐溝溢流浸濕木材", "邊緣金屬失效", "原始材料壽命末期", "通風潮濕", "撞擊或翻修損壞"],
    methodology: [
      "探查並標記損壞長度",
      "拆除失效構件",
      "安裝新封檐板/檐口材料",
      "按設計恢復通風開口",
      "協調檐溝重新安裝",
    ],
    materials: ["封檐板或包覆系統", "檐口底板", "緊固件和收邊", "包含時的油漆或飾面系統"],
    bcClimate: "雨季反覆檐溝溢流是低陸平原封檐板腐朽的常見原因。",
    whenAppropriate: "檐口構件損壞但屋面主區域仍可能可使用時。",
    whenOtherService: "若檐溝或屋頂邊緣系統造成或將再次造成損壞，宜一併處理。",
    process: [
      "探查並標記受損檐口長度",
      "規劃檐溝拆卸與重新安裝",
      "書面範圍含材料與飾面選擇",
      "拆除腐朽構件並安裝新材料",
      "恢復進氣開口並重新掛檐溝",
    ],
    solutionCategories: [
      {
        title: "封檐板恢復",
        body: "更換支撐檐溝和邊緣金屬的腐朽板材。",
      },
      {
        title: "檐口底板維修",
        body: "恢復檐口底面板和進氣開口。",
      },
      {
        title: "檐口協調",
        body: "與新構件同步安排檐溝和邊緣細部。",
      },
    ],
    repairVsReplace: {
      repair: "損壞局限時局部維修。",
      replace: "沿檐口連續腐朽時更換較長段落。",
    },
    scopeFactors: ["線性長度", "材料選擇", "高度/進場", "飾面要求", "檐溝互動"],
    faqs: [
      {
        question: "什麼是封檐板＋檐口底板維修／更換？",
        answer: "封檐板與檐口底板維修或更換恢復完成屋頂邊緣、支撐檐溝並有助於進氣通風的檐口組件。",
      },
      {
        question: "封檐板為什麼會腐爛？",
        answer:
          "本地常見順序是檐溝溢流或失效滴水邊在雨季反覆浸濕板材。油漆膜失效後木材保持潮濕，腐朽加速。修正水源與更換板材同樣重要。",
      },
      {
        question: "可以不拆檐溝就換封檐板嗎？",
        answer:
          "很少能做好。檐溝穿過封檐板固定，正確更換意味著拆卸並重新懸掛，也是修正坡度和固定到新完好板材的機會。",
      },
      {
        question: "檐口底板是閣樓通風系統的一部分嗎？",
        answer:
          "在大多數住宅中是的：檐口底板帶有向閣樓供氣的進氣開口。更換時必須保留或恢復進氣；美容升級時堵住會 quietly 造成閣樓潮濕問題。",
      },
      {
        question: "木材、鋁或複合封檐板該選哪種？",
        answer:
          "木材傳統且可修，但需飾面維護。包覆木材的鋁耐候但細部不當可能困住水分。複合材料抗腐且維護較少。暴露、預算和建築風格引導選擇。",
      },
    ],
    publicPriceHint:
      "檐口工程按線性長度、高度與進場、材料與飾面選擇及檐溝與維修的互動計價。申請評估以取得專案報價。",
  },
  "cedar-shake-repair-restoration": {
    title: "雪松木瓦維修／復原",
    shortTitle: "雪松維修",
    eyebrow: "高端木質屋頂",
    description: "在高端木質屋頂上更換損壞雪松木瓦，並清潔或復原局部區域。",
    definition: "雪松木瓦維修在更廣泛雪松屋頂仍值得維護時，恢復局部失效木瓦及相關細部。",
    forWhom: "雪松屋頂有局部開裂、缺失或翹曲木瓦的業主。",
    warningSigns: [
      "個別木瓦開裂或缺失",
      "局部翹曲",
      "苔蘚在木材上積留水分",
      "緊固件生鏽伴局部失效",
      "低坡雪松段雜物淤塞",
    ],
    evaluates: ["匹配木瓦供應", "剩餘主區域狀況", "清潔需求", "泛水接口", "是否更接近系統性更換"],
    causes: ["紫外線與濕乾循環", "生物生長", "撞擊", "不當先前維修", "仍值得維護的高端木屋頂老化"],
    methodology: [
      "採購匹配木瓦",
      "小心更換失效單元",
      "處理積留水分的生長和雜物",
      "複查工作區泛水",
      "記錄剩餘壽命指標",
    ],
    materials: ["適當等級雪松木瓦", "需要時的墊層補片", "兼容緊固件", "適合木屋頂的清潔方法"],
    bcClimate: "卑詩濕冷氣候對木屋頂要求很高；主區域仍基本完好時，維護可延長使用壽命。",
    whenAppropriate: "失效局限且更廣泛雪松主區域仍可維護時。",
    whenOtherService: "系統性翹曲、漏水和墊層失效指向完整雪松更換。",
    process: [
      "評估主區域、生長和失效位置",
      "採購適當等級匹配木瓦",
      "書面界定維修與清潔區域",
      "更換失效木瓦並處理積水雜物",
      "記錄剩餘壽命指標供規劃",
    ],
    solutionCategories: [
      {
        title: "選擇性木瓦更換",
        body: "在不丟棄可維護主區域的情況下恢復失效單元。",
      },
      {
        title: "局部清潔",
        body: "減少維修區周邊積留水分的生長和雜物。",
      },
      {
        title: "狀況分診",
        body: "區分可維護雪松與需討論完整更換的屋頂。",
      },
    ],
    repairVsReplace: {
      repair: "主區域仍值得維護時維修。",
      replace: "木屋頂磨損具系統性時更換。",
    },
    scopeFactors: ["維修面積", "木瓦匹配", "進場", "清潔範圍", "相鄰泛水需求"],
    faqs: [
      {
        question: "什麼是雪松木瓦維修／復原？",
        answer: "雪松木瓦維修在更廣泛雪松屋頂仍值得維護時，恢復局部失效木瓦及相關細部。",
      },
      {
        question: "雪松屋頂值得修還是該換？",
        answer:
          "決定因素是整個主區域及下方墊層狀況，而非最糟幾片木瓦。排水良好的主區域上局部開裂傾向維修；普遍翹曲、軟木瓦和多坡面反覆漏水傾向更換規劃。",
      },
      {
        question: "為什麼部分木瓦翹曲和開裂？",
        answer:
          "雪松隨反覆濕乾循環運動。向陽坡乾燥劇烈並開裂；陰涼潮濕坡保持濕潤並翹曲或長苔蘚。年齡、等級和原始固定方式影響出現早晚。",
      },
      {
        question: "處理能讓雪松更耐用嗎？",
        answer:
          "在此氣候下，保持雪松無苔蘚和雜物是最高價值維護，因困住水分會驅動腐朽。表面處理存在且逐案討論；它們補充良好排水和乾燥，而非取代。",
      },
      {
        question: "維修區能與現有雪松顏色匹配嗎？",
        answer:
          "新雪松起初呈金黃色，經季節暴露後風化為灰色，因此維修起初可見、逐漸融合。外觀關鍵時，施工前會討論位置與預期。",
      },
    ],
    publicPriceHint:
      "雪松維修反映維修面積、木瓦匹配、進場、清潔範圍及相鄰泛水需求。申請評估以取得專案報價。",
  },
  "flat-low-slope-roof-repair": {
    title: "平／低坡屋頂維修",
    shortTitle: "平屋頂維修",
    eyebrow: "低坡膜材",
    description: "平屋頂和低坡屋頂上 SBS、熱熔、TPO/EPDM 補片、泛水和穿透點維修。",
    definition: "平低坡屋頂維修處理膜材接縫、泛水、穿透點和局部膜材失效，無需更換整個低坡組件。",
    forWhom: "低坡屋頂有積水滲漏、接縫失效、基座漏水或膜材開口區域的業主。",
    warningSigns: [
      "接縫張開",
      "穿孔痕跡",
      "失效泛水盆或穿透口袋",
      "起泡伴持續滲漏",
      "排水口或泄水口無法控水",
    ],
    evaluates: ["膜材類型", "補片兼容性", "排水模式", "穿透點狀況", "保溫是否超出局部維修範圍受潮"],
    causes: ["膜材老化", "踩踏損壞", "積水應力", "女兒牆泛水位移", "不兼容先前補片"],
    methodology: [
      "識別膜材系統",
      "界定兼容補片方法",
      "維修泛水和穿透點",
      "在實際可行處改善局部排水細部",
      "系統耗盡時建議更換",
    ],
    materials: [
      "兼容膜材補片",
      "泛水金屬和配件",
      "穿透口袋或預製細部",
      "與系統相符的膠粘或熱工工藝",
    ],
    bcClimate: "漫長濕季和積水風險使接縫和排水細部在低陸平原低坡屋頂上至關重要。",
    whenAppropriate: "失效局限且膜材主區域仍可接受耐久補片時。",
    whenOtherService: "保溫飽和或系統性接縫失效通常意味著更換規劃。",
    process: [
      "識別膜材系統和失效點",
      "損壞周邊潮濕與排水複查",
      "書面界定兼容補片和細部範圍",
      "以系統適當方法執行維修",
      "複查排水行為並記錄工作",
    ],
    solutionCategories: [
      {
        title: "膜材補片",
        body: "以兼容方法維修接縫、穿孔和局部主區域損壞。",
      },
      {
        title: "穿透點與基座細部",
        body: "恢復屋頂開口處防水。",
      },
      {
        title: "排水導向修正",
        body: "處理在薄弱點積水的細部。",
      },
    ],
    repairVsReplace: {
      repair: "膜材主區域仍可使用時維修。",
      replace: "系統飽和或反覆失效時更換。",
    },
    scopeFactors: ["膜材類型", "維修面積", "進場", "穿透點數量", "排水條件"],
    faqs: [
      {
        question: "什麼是平／低坡屋頂維修？",
        answer: "平低坡屋頂維修處理膜材接縫、泛水、穿透點和局部膜材失效，無需更換整個低坡組件。",
      },
      {
        question: "為什麼平屋頂補片要看膜材類型？",
        answer:
          "每種膜材粘合方式不同：SBS 為熱熔或胶粘改性瀝青，TPO 為熱焊，EPDM 依賴膠粘和胶带。錯誤材料或方法的補片無法耐久粘合。誤識補片是反覆漏水的常見原因。",
      },
      {
        question: "平屋頂積水有問題嗎？",
        answer:
          "雨後長時間滞留的水會應力接縫、加速膜材老化並放大小缺陷。部分原因可維修：堵塞排水口、局部低點，而結構坡度問題則指向更換級討論。",
      },
      {
        question: "冬天能修平屋頂嗎？",
        answer:
          "通常可以，但需調整方法：部分材料和膠粘有溫度限制，表面須乾燥。緊急穩定幾乎總是可行；某些永久細部可能等待適宜天氣窗口。",
      },
      {
        question: "如何知道膜下保溫是否受潮？",
        answer:
          "症狀包括持續的室內潮濕、膜材起泡和腳下鬆軟區域；確認可能需要潮濕掃描或試切。受潮保溫很重要，因在其上補片會困住水分並持續惡化。",
      },
    ],
    publicPriceHint:
      "平屋頂維修價格取決於膜材類型、維修面積、穿透點數量、排水條件和進場。申請評估以取得專案報價。",
  },
  "metal-roof-repair-maintenance": {
    title: "金屬屋頂維修／維護",
    shortTitle: "金屬維修",
    eyebrow: "金屬系統",
    description: "金屬屋面系統的緊固件、泛水、密封膠、板材和穿透點維修。",
    definition:
      "金屬屋頂維修與維護在保留可用板材的同時，修正緊固件、泛水、密封膠、板材損壞和穿透點。",
    forWhom: "金屬屋頂有鬆動緊固件、失效收口、穿透漏水或局部板材損壞的業主。",
    warningSigns: [
      "明釘系統緊固件退出",
      "泡沫收口失效",
      "穿透套管漏水",
      "細部密封膠開裂",
      "泛水處切邊開始腐蝕",
    ],
    evaluates: ["板材型材", "塗層狀況", "緊固件類型", "收口與泛水細部", "損壞是否具系統性"],
    causes: ["熱循環", "緊固件類型不當", "密封膠老化", "撞擊凹痕", "沿海暴露影響"],
    methodology: [
      "識別系統類型",
      "按需修正緊固件和收口",
      "維修泛水和穿透點",
      "處理局部板材損壞",
      "需要金屬細部時避免僅表面填縫",
    ],
    materials: ["兼容緊固件和墊圈", "收口條", "泛水金屬", "僅在適當時的補漆系統"],
    bcClimate: "沿海暴露和熱位移使緊固件和泛水成為金屬屋頂上反覆的維護點。",
    whenAppropriate: "板材仍完好且問題在構件層級時。",
    whenOtherService: "系統性塗層失效或過時型材可能導向更換討論。",
    process: [
      "識別板材系統和固定方式",
      "檢查緊固件、收口、泛水和穿透點",
      "書面區分維護與維修",
      "以兼容構件修正細部",
      "將屋頂作為運作系統複查",
    ],
    solutionCategories: [
      {
        title: "緊固件與收口維護",
        body: "恢復防風雨的機械細部。",
      },
      {
        title: "泛水與穿透點維修",
        body: "修正進水的接口。",
      },
      {
        title: "板材級修正",
        body: "在不丟棄完好主區域的情況下處理局部損壞。",
      },
    ],
    repairVsReplace: {
      repair: "金屬主區域仍可使用時維護和維修。",
      replace: "系統在經濟或技術上不再可維護時更換。",
    },
    scopeFactors: ["系統類型", "進場", "細部數量", "塗層狀況", "穿透點密度"],
    faqs: [
      {
        question: "什麼是金屬屋頂維修／維護？",
        answer:
          "金屬屋頂維修與維護在保留可用板材的同時，修正緊固件、泛水、密封膠、板材損壞和穿透點。",
      },
      {
        question: "為什麼金屬屋頂螺絲會退出？",
        answer:
          "明釘系統依賴隨熱循環移動的墊圈螺絲；年久可能鬆動、墊圈硬化。修正，有時用加大替換件，是這些系統的正常維護，不代表屋頂已失效。",
      },
      {
        question: "金屬屋頂通風管漏水常見嗎？",
        answer:
          "穿透點是金屬屋頂最常見漏點。防水套和套管比板材老化更快，須適應板材移動。以更換兼容細部的防水套通常可解決，無需動主區域。",
      },
      {
        question: "金屬板凹痕能修嗎？",
        answer:
          "不損及塗層或接縫的外觀性凹痕常可保留。破壞塗層、打開接縫或積水的損壞則需要板材級維修或更換受影響板片，大多數型材上這是可行的。",
      },
      {
        question: "直立鎖邊和明釘金屬維護有何不同？",
        answer:
          "直立鎖邊在夾具上隱藏固定，日常墊圈維護需求大幅減少；重點轉向泛水、穿透點和收口密封膠接縫。明釘系統則需定期檢查緊固件。",
      },
    ],
    publicPriceHint:
      "金屬屋頂工程按系統類型、需修正細部數量、塗層狀況和進場界定。申請評估以取得專案報價。",
  },
  "cedar-shake-roof-replacement": {
    title: "雪松木瓦屋頂更換",
    shortTitle: "雪松更換",
    eyebrow: "高端木質重鋪",
    description: "為有意保留木質屋頂建築特色的住宅提供完整高端雪松木瓦重鋪。",
    definition:
      "雪松木瓦屋頂更換是使用雪松木瓦或指定木質屋面組件的完整重鋪，包括基層準備及高端木系統的正確細部。",
    forWhom: "當系統性雪松磨損使維修不再合理，仍決定延續木質屋頂特色的業主。",
    warningSigns: [
      "系統性翹曲和開裂",
      "多坡面反覆漏水",
      "雪松下方墊層失效",
      "對剩餘壽命的買賣或規劃顧慮",
      "建築上有意保留木質屋面",
    ],
    evaluates: ["木瓦等級與型材", "墊層策略", "通風與乾燥", "防火及項目背景考量", "幾何複雜程度"],
    causes: ["完整使用壽命完成", "長期潮濕", "多坡面風災損失", "隨時間發現的基層問題"],
    methodology: [
      "評估基層和組件需求",
      "指定木瓦系統和墊層做法",
      "拆除期間保護物業",
      "將現場和細部作為完整木屋頂組件安裝",
      "複查雪松維護預期",
    ],
    materials: ["指定等級雪松木瓦", "墊層系統", "與木屋頂兼容的泛水", "緊固件和屋脊細部"],
    bcClimate: "卑詩省高端木屋頂需要誠實的維護預期；當維修跟不上系統性磨損時，更換重置組件。",
    whenAppropriate: "業主希望完整雪松系統且現有木屋頂已至可一致服務的末期時。",
    whenOtherService: "仍強健主區域上的局部失效可能仍適合雪松維修。",
    process: [
      "評估現有木屋頂和基層",
      "指定木瓦等級、墊層和細部",
      "書面範圍與物業保護計劃",
      "拆除、基層準備和安裝",
      "完工複查與維護指引",
    ],
    solutionCategories: [
      {
        title: "完整雪松現場安裝",
        body: "以完整屋頂系統安裝新木瓦。",
      },
      {
        title: "基層與墊層準備",
        body: "處理木材下方的屋面板和排水層。",
      },
      {
        title: "木屋頂細部",
        body: "為雪松性能細部處理泛水和邊緣。",
      },
    ],
    repairVsReplace: {
      repair: "僅局部時維修。",
      replace: "磨損具系統性且仍選擇木質系統時完整雪松重鋪。",
    },
    scopeFactors: ["屋面面積", "木瓦規格", "基層維修", "進場", "項目複雜程度"],
    faqs: [
      {
        question: "什麼是雪松木瓦屋頂更換？",
        answer:
          "雪松木瓦屋頂更換是使用雪松木瓦或指定木質屋面組件的完整重鋪，包括基層準備及高端木系統的正確細部。",
      },
      {
        question: "雪松屋頂在 BC 能用多久？",
        answer:
          "使用壽命因等級、暴露、細部和尤其是維護而異，單一數字會誤導。在此區域，建造和維護良好的雪松屋頂常可服務數十年；疏於維護者則早得多失效。評估時會說明您坡面和遮蔭的現實預期。",
      },
      {
        question: "雪松木瓦和雪松木片有什麼區別？",
        answer:
          "木瓦劈裂較厚，有高端木屋頂常見的質感面；木片鋸切較薄、更均勻。選擇影響外觀、細部和成本，在規格階段確定。",
      },
      {
        question: "雪松屋頂需要特殊墊層嗎？",
        answer:
          "木屋頂需要既能排水又讓雪松乾燥的墊層策略，因雪松在無法呼吸時表現差。層間墊層和適當檐口保護通常是正確組件的典型要素。",
      },
      {
        question: "在大溫哥華雪松仍是合理的屋頂選擇嗎？",
        answer:
          "對合適的住宅仍是經過深思的高端選擇：建築、社區特色和業主對維護的承諾都重要。濕冷氣候要求誠實的保養預期；偏好極少維護的業主通常更適合其他高端系統。",
      },
    ],
    publicPriceHint:
      "雪松重鋪價格反映屋面面積與幾何、木瓦規格、基層維修和進場。申請評估以取得專案報價。",
  },
  "metal-roof-replacement": {
    title: "金屬屋頂更換",
    shortTitle: "金屬屋頂更換",
    eyebrow: "高端金屬重鋪",
    description: "為適合的陡坡建築提供直立鎖邊或其他高端金屬重鋪。",
    definition:
      "金屬屋頂更換是在評估幾何形態、基層、墊層和建築意圖後，安裝新的金屬屋面系統：通常為直立鎖邊系統。",
    forWhom: "為適合的幾何形態、建築風格和長期規劃，選擇高端金屬系統的業主。",
    warningSigns: [
      "現有覆蓋層已至末期且選定金屬作為新系統",
      "建築適合直立鎖邊",
      "希望獲得高端覆蓋層外觀",
      "複雜幾何形態更適合金屬板表達",
    ],
    evaluates: ["型材選擇", "墊層和基層", "防冷凝方案", "泛水和穿透點", "聲學和設計預期"],
    causes: ["計劃中的系統升級", "現有屋頂使用壽命末期", "設計翻修", "與金屬組件相符的性能目標"],
    methodology: [
      "確認幾何形態適用性",
      "確定板材系統和墊層",
      "準備基層",
      "將板材和泛水作為完整系統安裝",
      "有意細部處理屋脊、邊緣和穿透點",
    ],
    materials: ["直立鎖邊或指定金屬板", "墊層系統", "系統所需的夾具/緊固件", "泛水和收口件"],
    bcClimate:
      "西海岸建築常與直立鎖邊金屬相得益彰，但在潮濕天氣中，墊層、通風和細部仍決定實際性能。",
    whenAppropriate: "當金屬為選定系統且項目範圍為完整重鋪時。",
    whenOtherService: "若現有金屬屋頂整體仍完好，構件級維修可能仍適用。",
    process: [
      "確認幾何形態和系統適用性",
      "確定板材型材、墊層和細部",
      "書面工程範圍，記錄設計決策",
      "基層準備和板材安裝",
      "細部複核和完工巡查",
    ],
    solutionCategories: [
      {
        title: "直立鎖邊系統",
        body: "在指定時採用隱藏式固定的高端板型外觀。",
      },
      {
        title: "墊層與基層準備",
        body: "在金屬下方構建排水基礎層。",
      },
      {
        title: "建築細部",
        body: "邊緣、天溝和牆體細部兼顧性能與外觀。",
      },
    ],
    repairVsReplace: {
      repair: "當板材仍完好時維修現有金屬屋頂。",
      replace: "當新項目目標是新金屬系統，或舊覆蓋層已完工時進行更換。",
    },
    scopeFactors: ["屋頂面積和幾何形態", "板材型材", "墊層選擇", "進場條件", "穿透點複雜程度"],
    faqs: [
      {
        question: "什麼是金屬屋頂更換？",
        answer:
          "金屬屋頂更換是在評估幾何形態、基層、墊層和建築意圖後，安裝新的金屬屋面系統：通常為直立鎖邊系統。",
      },
      {
        question: "金屬屋頂下雨會很吵嗎？",
        answer:
          "在實心屋面板、墊層和保溫下方的住宅重鋪上，雨聲通常遠小於多數人預期：響亮印象多來自架空框架上的板材。系統選擇時仍會討論聲學預期。",
      },
      {
        question: "直立鎖邊和明釘金屬有什麼區別？",
        answer:
          "直立鎖邊通過凸起接縫下方的夾具隱藏固定，允許板材熱脹冷縮並將墊圈螺釘置於天氣之外。明釘板成本較低，但需長期維護緊固件。高端住宅工程通常推薦直立鎖邊。",
      },
      {
        question: "可以在現有瓦片上直接鋪金屬屋頂嗎？",
        answer:
          "有時結構上可行，但通常更傾向拆除，以便檢查屋面板並正確建造墊層和泛水系統。困住舊覆蓋層也會使日後診斷更困難。",
      },
      {
        question: "金屬屋頂適合複雜屋頂造型嗎？",
        answer:
          "適合，但複雜程度決定金屬項目的成敗：屋脊、天溝、老虎窗和死衚衕細部需要熟練的金屬工藝並增加成本。幾何複核是判斷金屬是否適合住宅的第一步。",
      },
    ],
    publicPriceHint:
      "金屬重鋪價格取決於屋頂幾何形態、板材型材、墊層策略和穿透點複雜程度。請申請評估以獲取書面工程範圍。",
  },
  "flat-roof-replacement-sbs-tpo-pvc": {
    title: "平屋頂更換：SBS / TPO / PVC",
    shortTitle: "平屋頂更換",
    eyebrow: "低坡重鋪",
    description: "使用 SBS、TPO、PVC 或其他指定膜材系統，完成住宅或商業低坡屋頂的整體重鋪。",
    definition:
      "平屋頂更換拆除或覆蓋現有低坡屋頂組件，並安裝根據排水、基層和項目要求選定的新膜材系統。",
    forWhom: "低坡屋頂出現反覆膜材失效、保溫受潮、系統過時，或配合外部翻修的業主。",
    warningSigns: [
      "多處膜材失效",
      "保溫層受潮",
      "系統過時或無法維護",
      "配合外部翻修工程",
      "長期積水且系統整體磨損",
    ],
    evaluates: ["膜材類型選項", "保溫和基層需求", "排水口和洩水口", "女兒牆和穿透點", "進場和保護"],
    causes: [
      "膜材使用壽命末期",
      "保溫飽和",
      "排水設計侷限",
      "紫外線和熱老化",
      "穿透點密集且細部失效累積",
    ],
    methodology: [
      "評估組件，在需要時調查受潮部位",
      "為項目選定膜材策略",
      "按範圍拆除或覆蓋",
      "按順序安裝保溫和膜材層",
      "將排水口、女兒牆和穿透點作為系統細部處理",
    ],
    materials: [
      "SBS、TPO、PVC 或其他指定膜材",
      "按範圍的保溫和蓋板",
      "排水和泛水配件",
      "與系統相符的膠粘劑、緊固件或熱工工藝",
    ],
    bcClimate: "大溫哥華多雨環境中，低坡屋頂更依賴排水設計和穩健的膜材細部，而非營銷標籤本身。",
    whenAppropriate: "當低坡組件已耗盡，或作為更廣泛項目的一部分進行更新時。",
    whenOtherService: "若膜材現場仍完好，局部膜材維修可能仍可行。",
    process: [
      "組件評估，在需要時進行受潮調查",
      "膜材系統選擇和書面工程範圍",
      "按範圍拆除或覆蓋現有組件",
      "按順序安裝保溫和膜材",
      "排水口、女兒牆和穿透點細部複核",
    ],
    solutionCategories: [
      {
        title: "膜材系統選擇",
        body: "根據項目條件選擇 SBS、TPO、PVC 或其他指定組件。",
      },
      {
        title: "組件更換",
        body: "按批准範圍處理膜材及相關層次。",
      },
      {
        title: "排水與細部",
        body: "排水口、女兒牆和穿透點作為核心防水工程處理。",
      },
    ],
    repairVsReplace: {
      repair: "若膜材現場仍可使用則進行維修。",
      replace: "當系統已耗盡時進行完整低坡更換。",
    },
    scopeFactors: ["面積和幾何形態", "膜材類型", "保溫需求", "穿透點密度", "進場和清運"],
    faqs: [
      {
        question: "使用 SBS、TPO 或 PVC 的平屋頂更換是什麼？",
        answer:
          "平屋頂更換拆除或覆蓋現有低坡屋頂組件，並安裝根據排水、基層和項目要求選定的新膜材系統。",
      },
      {
        question: "SBS、TPO 還是 PVC，哪種平屋頂膜材最好？",
        answer:
          "沒有一種普遍適用於所有情況的最佳選擇。雙層 SBS 是當地常用、冗餘度高的系統；TPO 和 PVC 為熱焊單層系統，重量較輕並有反射選項，PVC 還具有較強的耐化學性。基層、排水、屋頂使用方式和預算決定選擇。",
      },
      {
        question: "應該拆除還是覆蓋現有平屋頂？",
        answer:
          "若現有組件乾燥完好，覆蓋可節省成本和清運；但若保溫受潮或已有多個層次，拆除是更誠實的路徑。決策前的受潮調查可避免把問題封在裡面。",
      },
      {
        question: "平屋頂更換有多大幹擾？",
        answer:
          "預計會有噪音、熱熔或膠粘作業時的氣味、分階段材料上料和受保護的通道。住戶通常可以留在家中，書面工程範圍會事先說明工序、工作時間和保護措施。",
      },
      {
        question: "平屋頂更換時能改善排水嗎？",
        answer:
          "更換是糾正長期積水的合適時機：錐形保溫、增設或移位排水口以及洩水口工程，可以設計進新組件，而不是事後補救。",
      },
    ],
    publicPriceHint:
      "低坡更換價格取決於面積、膜材選擇、保溫需求、排水工程和穿透點密度。請申請評估。",
  },
  "roof-repair": {
    title: "緊急屋頂漏水維修",
    shortTitle: "緊急漏水維修",
    eyebrow: "主動進水",
    description:
      "優先漏水診斷：在排期允許時當日到場，針對水從大溫哥華住宅屋頂系統進入的情況，進行臨時或永久性局部維修。",
    definition:
      "緊急屋頂漏水維修是針對屋頂覆蓋層、泛水、穿透點或相關接口處主動或快速發展的進水，進行重點排查與穩定處理。在條件允許時，工作可包括臨時防水和永久性局部維修。",
    forWhom:
      "出現持續滴水、雨天突發天花水漬、暴風雨後閣樓受潮，或需要在規劃更大範圍工程前進行安全診斷和控制的業主。",
    warningSigns: [
      "降雨期間或之後持續滴水或潮濕",
      "天花、牆體或燈具上出現新的或擴大的水漬",
      "保溫層、屋面板或閣樓構架受潮",
      "水沿天溝、煙囪、天窗或管道通風口蔓延",
      "每次天氣事件後室內損壞持續加重",
    ],
    evaluates: [
      "室內證據及可能的水流路徑",
      "受影響區域周邊的屋面覆蓋層",
      "泛水、穿透點、天溝和牆體接口",
      "臨時保護還是永久維修的需求",
      "相鄰材料能否支持耐久施工",
    ],
    causes: [
      "泛水或管道防水套密封失效",
      "被風掀起或缺失的覆蓋材料",
      "雜物堵塞排水導致積水",
      "暴風雨損壞屋脊、邊緣或過渡部位",
      "以往修補未處理真正源頭",
    ],
    methodology: [
      "安全記錄室內和室外證據",
      "追查最可能的進水路徑，而不只是水漬位置",
      "在天氣或進場條件需要時以臨時防水穩定",
      "在周邊組件仍可使用時，以兼容材料進行局部永久維修",
      "說明殘餘風險及是否應進行更大範圍工程",
    ],
    materials: [
      "與現有系統兼容的覆蓋材料",
      "在適當位置的墊層或膜材補丁",
      "泛水金屬件和穿透防水套",
      "密封膠僅作為正確細部的一部分，而非唯一修復",
      "需要時的臨時防水布或幹鋪材料",
    ],
    bcClimate:
      "大溫哥華頻繁降雨意味著小缺陷也會反覆造成室內損壞。及時診斷比表面修補、卻未解決真正路徑更為重要。",
    whenAppropriate: "當進水侷限、周邊屋頂仍可正常使用，且能安全執行耐久維修或臨時防水時。",
    whenOtherService:
      "覆蓋層大範圍失效、多處獨立漏水，或屋頂系統已接近末期時，可能需要更廣泛的維修規劃、僅做暴風雨臨時防水，或討論整體更換。",
    process: [
      "致電或發送您所觀察到的情況及照片",
      "分診與排期：在有空檔時當日到場",
      "在屋頂上溯源診斷，並記錄室內和室外情況",
      "按條件允許進行臨時防水或局部永久維修",
      "書面總結髮現、已完成工作及建議的後續步驟",
    ],
    solutionCategories: [
      {
        title: "源頭診斷",
        body: "將室內證據與最可能進水的室外細部聯繫起來，包括覆蓋層下方的橫向水流。",
      },
      {
        title: "臨時防水",
        body: "在暴風雨損壞後，或永久材料無法在同一次上門完成時，穩定開口部位。",
      },
      {
        title: "局部永久維修",
        body: "以與周邊屋頂系統兼容的材料，更換或重新細部處理失效構件。",
      },
    ],
    repairVsReplace: {
      repair: "當已確定單一失效區域且周邊材料仍足夠完好、可接受兼容施工時，通常適合重點維修。",
      replace: "當檢查發現大範圍老化、多處獨立失效，或組件無法支持耐久補丁時，更換會進入討論。",
    },
    scopeFactors: [
      "進場條件和屋頂高度",
      "天氣窗口",
      "隱蔽損壞範圍",
      "材料兼容性",
      "涉及的穿透點數量",
      "臨時保護需求",
    ],
    faqs: [
      {
        question: "什麼是緊急屋頂漏水維修？",
        answer:
          "緊急屋頂漏水維修是針對屋頂覆蓋層、泛水、穿透點或相關接口處主動或快速發展的進水，進行重點排查與穩定處理。在條件允許時，工作可包括臨時防水和永久性局部維修。",
      },
      {
        question: "下雨時還能修屋頂漏水嗎？",
        answer:
          "在持續降雨時，安全的第一步通常是臨時防水：鋪防水布或密封進水區域，因為大多數永久屋面材料需要乾燥條件才能正確粘結。永久維修隨後在合適的天氣窗口完成。",
      },
      {
        question: "為什麼天花水漬離可見屋頂損壞很遠？",
        answer:
          "水常從一個細部進入，再沿墊層、結構構件或屋面板移動後才在室內顯現。因此診斷會追查屋頂上可能的進水路徑，而不只是直接修補水漬上方。",
      },
      {
        question: "你們在大溫哥華提供當日屋頂漏水維修嗎？",
        answer:
          "Revolin 會在排期和日光條件允許時，儘量當日處理主動漏水，但到場時間不作保證。分享照片和對所見情況的清晰描述，有助於分診溝通。",
      },
      {
        question: "漏水維修會是臨時的還是永久的？",
        answer:
          "這取決於天氣、材料供應和排查結果。若首次上門能安全完成耐久的局部維修，就會這樣做；否則先用臨時防水穩定屋頂，再以書面形式確定永久工程範圍。",
      },
      {
        question: "緊急屋頂漏水維修費用是多少？",
        answer:
          "價格取決於屋頂高度和進場條件、隱蔽損壞範圍，以及除維修外是否還需要臨時保護。Revolin 在評估後提供針對具體項目的報價，而不是未經現場查看就報價。",
      },
    ],
    publicPriceHint:
      "緊急工程範圍取決於進場條件、施工時的天氣、水已蔓延的距離，以及永久維修前是否需要臨時保護。請申請評估以獲取針對具體項目的報價。",
  },
  "roof-replacement": {
    title: "瀝青瓦屋頂更換",
    shortTitle: "瀝青瓦更換",
    eyebrow: "陡坡重鋪",
    description: "為大溫哥華陡坡住宅提供完整的瀝青瓦拆除、墊層、瓦片、泛水整合及廢料清運。",
    definition:
      "瀝青瓦屋頂更換是一次完整的陡坡重鋪：拆除現有瓦面，處理基層和墊層需求，安裝新的瓦片系統，並重新整合泛水和穿透點。",
    forWhom: "瓦片已大範圍磨損、反覆維修，或不再符合住宅剩餘使用計劃的業主。",
    warningSigns: [
      "瓦片捲曲、開裂或大面積顆粒脫落",
      "多個已維修屋面仍反覆失效",
      "瓦片變脆、無法再重新密封",
      "天溝、屋脊和簷口老化",
      "翻修工程使整體重鋪成為更合理的選擇",
    ],
    evaluates: [
      "現有層數和屋面板狀況",
      "坡度、天溝、老虎窗和幾何形態",
      "通風及閣樓受潮跡象",
      "泛水和穿透點清單",
      "進場、保護和廢料清運",
    ],
    causes: [
      "正常使用壽命末期風化",
      "長期苔蘚和潮濕",
      "累積的風損",
      "通風不良加速老化",
      "以往安裝細部薄弱",
    ],
    methodology: [
      "記錄狀況和可選方案",
      "規劃拆除、保護和清運",
      "按發現情況維修屋面板並書面說明",
      "按順序安裝墊層、泛水和現場瓦片",
      "將屋脊、天溝、邊緣和穿透點作為完整組件細部處理",
    ],
    materials: [
      "建築型瀝青瓦",
      "墊層及在適當位置的冰壩/防水膜",
      "天溝、牆體和穿透點泛水",
      "屋脊和通風配件",
      "緊固件和邊緣構件",
    ],
    bcClimate: "沿海降雨和苔蘚生長使墊層、天溝細部和通風在大溫地區瀝青瓦屋頂上尤為重要。",
    whenAppropriate: "當瓦片老化具有系統性，或繼續局部修補仍會留下其他近期失效風險時。",
    whenOtherService: "若周邊系統仍可正常使用，孤立風損可能仍適合局部維修。",
    process: [
      "評估覆蓋層、屋面板指標和通風",
      "書面工程範圍，含材料和細部選項",
      "拆除並做好物業保護和廢料清運",
      "按順序進行屋面板維修、墊層、泛水和瓦片安裝",
      "最終巡查和完工複核",
    ],
    solutionCategories: [
      {
        title: "建築型複合瀝青瓦",
        body: "根據幾何形態、暴露條件和設計意圖選擇的立體住宅瓦型。",
      },
      {
        title: "墊層與排水細部",
        body: "協調現場瓦片與天溝、簷口和過渡部位。",
      },
      {
        title: "泛水重新整合",
        body: "牆體、煙囪、通風口和邊緣作為新組件的一部分進行細部處理。",
      },
    ],
    repairVsReplace: {
      repair: "當損壞侷限且匹配材料能幹淨整合時，局部維修可行。",
      replace: "當磨損廣泛或組件無法接受耐久補丁時，更換更為合理。",
    },
    scopeFactors: [
      "屋頂面積和坡度",
      "層數",
      "屋面板維修",
      "天溝和老虎窗複雜程度",
      "材料選擇",
      "進場和清運",
    ],
    faqs: [
      {
        question: "什麼是瀝青瓦屋頂更換？",
        answer:
          "瀝青瓦屋頂更換是一次完整的陡坡重鋪：拆除現有瓦面，處理基層和墊層需求，安裝新的瓦片系統，並重新整合泛水和穿透點。",
      },
      {
        question: "瀝青瓦屋頂更換需要多長時間？",
        answer:
          "大多數獨立住宅重鋪以天計而非以周計，但工期取決於屋頂大小、坡度、天氣窗口，以及拆除過程中發現的屋面板維修量。書面工程範圍會在開工前說明預期。",
      },
      {
        question: "可以在舊瓦片上直接鋪新瓦嗎？",
        answer:
          "疊鋪有時在技術上可行，但通常更傾向拆除：這樣可以檢查屋面板、正確安裝墊層和泛水，並避免增加重量、把問題困在新覆蓋層下方。",
      },
      {
        question: "拆除時發現腐爛屋面板怎麼辦？",
        answer:
          "發現的屋面板損壞會在新系統安裝前記錄並維修。清晰的工程範圍會事先說明屋面板工作如何計量和定價，使中途發現透明處理，而非意外追加。",
      },
      {
        question: "哪種瓦片最適合大溫哥華天氣？",
        answer:
          "建築型複合瀝青瓦是當地常見的住宅選擇。在此氣候下，墊層質量、天溝和邊緣細部以及閣樓通風，對實際使用壽命的影響往往不亞於瓦片產品本身。",
      },
    ],
    publicPriceHint:
      "項目價格取決於屋頂面積、坡度、層數、屋面板維修、天溝和老虎窗複雜程度及材料選擇。請申請評估以獲取書面工程範圍。",
  },
  "asphalt-shingle-roofing": {
    title: "建築型瀝青瓦屋頂",
    shortTitle: "瀝青瓦",
    eyebrow: "陡坡屋頂系統",
    description: "適用於大溫哥華許多具備相應陡坡屋頂系統住宅的實用、兼顧設計的屋頂類別。",
    definition:
      "瀝青瓦屋頂更換是一次完整的陡坡重鋪：拆除現有瓦面，處理基層和墊層需求，安裝新的瓦片系統，並重新整合泛水和穿透點。",
    forWhom:
      "建築型瀝青瓦適合許多坡屋頂住宅，滿足業主對成熟系統、豐富外觀選擇及日後便於維護的需求。",
    warningSigns: [
      "顆粒脫落或胎基外露",
      "瓦片捲曲、開裂或移位",
      "天溝、邊緣或穿透點周邊損壞",
      "苔蘚遮蓋屋面",
      "多個屋面反覆維修",
    ],
    evaluates: [
      "屋頂坡度和幾何形態",
      "經適當檢查後的屋面板狀況",
      "墊層、檐口和天溝做法",
      "通風及進、排氣路徑",
      "泛水接口與排水",
    ],
    causes: [
      "正常使用壽命末期風化",
      "長期苔蘚和潮濕",
      "累積的風損",
      "通風不良加速老化",
      "以往安裝細部薄弱",
    ],
    methodology: [
      "記錄狀況和可選方案",
      "規劃拆除、保護和清運",
      "按發現情況維修屋面板並書面說明",
      "按順序安裝墊層、泛水和現場瓦片",
      "將屋脊、天溝、邊緣和穿透點作為完整組件細部處理",
    ],
    materials: [
      "建築型瀝青瓦",
      "墊層及在適當位置的冰壩/防水膜",
      "天溝、牆體和穿透點泛水",
      "屋脊和通風配件",
      "緊固件和邊緣構件",
    ],
    bcClimate: "沿海降雨和苔蘚生長使墊層、天溝細部和通風在大溫地區瀝青瓦屋頂上尤為重要。",
    whenAppropriate: "當瓦片老化具有系統性，或繼續局部修補仍會留下其他近期失效風險時。",
    whenOtherService: "若周邊系統仍可正常使用，孤立風損可能仍適合局部維修。",
    process: [
      "評估覆蓋層、屋面板指標和通風",
      "書面工程範圍，含材料和細部選項",
      "拆除並做好物業保護和廢料清運",
      "按順序進行屋面板維修、墊層、泛水和瓦片安裝",
      "最終巡查和完工複核",
    ],
    solutionCategories: [
      {
        title: "建築型複合瀝青瓦",
        body: "廣泛用於住宅陡坡屋頂的立體瓦型，具體選擇取決於系統和設計要求。",
      },
      {
        title: "墊層與排水細部",
        body: "可見瓦片只是協調系統的一層，完整系統還包括搭接、天溝、邊緣和交接部位。",
      },
      {
        title: "泛水整合",
        body: "牆體、煙囪、管道通風口和屋頂構件的接口，需要與瓦面按明確順序施工。",
      },
    ],
    repairVsReplace: {
      repair: "若能在不損壞脆弱周邊瓦面的情況下整合匹配且兼容的瓦片，局部維修可能可行。",
      replace: "若瓦片普遍變脆、磨損或多個屋面均受影響，更換可能更合適。",
    },
    scopeFactors: [
      "屋頂坡度和總面積",
      "天溝、老虎窗和穿透點數量",
      "現有層數及屋面板維修",
      "瓦型和配件選擇",
      "通風和泛水工程",
      "進場和現場保護",
    ],
    faqs: [
      {
        question: "建築型瀝青瓦適合所有坡屋頂嗎？",
        answer:
          "不適合。必須考慮坡度、基層、暴露條件、幾何形態和當地要求。選擇產品前，應將屋頂作為完整系統進行評估。",
      },
      {
        question: "顏色會影響建築效果嗎？",
        answer:
          "會。屋頂顏色會與外牆、飾邊、建築體量及街道可見度相互作用。應結合實際建築查看樣品，而不應只看屏幕。",
      },
    ],
    publicPriceHint:
      "項目價格取決於屋頂面積、坡度、層數、屋面板維修、天溝和老虎窗複雜程度及材料選擇。請申請評估以獲取書面工程範圍。",
  },
  "metal-roofing": {
    title: "金屬屋頂更換",
    shortTitle: "金屬屋頂更換",
    eyebrow: "高端金屬重鋪",
    description: "為適合的陡坡建築提供直立鎖邊或其他高端金屬重鋪。",
    definition:
      "金屬屋頂更換是在評估幾何形態、基層、墊層和建築意圖後，安裝新的金屬屋面系統：通常為直立鎖邊系統。",
    forWhom: "為適合的幾何形態、建築風格和長期規劃，選擇高端金屬系統的業主。",
    warningSigns: [
      "現有覆蓋層已至末期且選定金屬作為新系統",
      "建築適合直立鎖邊",
      "希望獲得高端覆蓋層外觀",
      "複雜幾何形態更適合金屬板表達",
    ],
    evaluates: ["型材選擇", "墊層和基層", "防冷凝方案", "泛水和穿透點", "聲學和設計預期"],
    causes: ["計劃中的系統升級", "現有屋頂使用壽命末期", "設計翻修", "與金屬組件相符的性能目標"],
    methodology: [
      "確認幾何形態適用性",
      "確定板材系統和墊層",
      "準備基層",
      "將板材和泛水作為完整系統安裝",
      "有意細部處理屋脊、邊緣和穿透點",
    ],
    materials: ["直立鎖邊或指定金屬板", "墊層系統", "系統所需的夾具/緊固件", "泛水和收口件"],
    bcClimate:
      "西海岸建築常與直立鎖邊金屬相得益彰，但在潮濕天氣中，墊層、通風和細部仍決定實際性能。",
    whenAppropriate: "當金屬為選定系統且項目範圍為完整重鋪時。",
    whenOtherService: "若現有金屬屋頂整體仍完好，構件級維修可能仍適用。",
    process: [
      "確認幾何形態和系統適用性",
      "確定板材型材、墊層和細部",
      "書面工程範圍，記錄設計決策",
      "基層準備和板材安裝",
      "細部複核和完工巡查",
    ],
    solutionCategories: [
      {
        title: "直立鎖邊系統",
        body: "在指定時採用隱藏式固定的高端板型外觀。",
      },
      {
        title: "墊層與基層準備",
        body: "在金屬下方構建排水基礎層。",
      },
      {
        title: "建築細部",
        body: "邊緣、天溝和牆體細部兼顧性能與外觀。",
      },
    ],
    repairVsReplace: {
      repair: "當板材仍完好時維修現有金屬屋頂。",
      replace: "當新項目目標是新金屬系統，或舊覆蓋層已完工時進行更換。",
    },
    scopeFactors: ["屋頂面積和幾何形態", "板材型材", "墊層選擇", "進場條件", "穿透點複雜程度"],
    faqs: [
      {
        question: "什麼是金屬屋頂更換？",
        answer:
          "金屬屋頂更換是在評估幾何形態、基層、墊層和建築意圖後，安裝新的金屬屋面系統：通常為直立鎖邊系統。",
      },
      {
        question: "金屬屋頂下雨會很吵嗎？",
        answer:
          "在實心屋面板、墊層和保溫下方的住宅重鋪上，雨聲通常遠小於多數人預期：響亮印象多來自架空框架上的板材。系統選擇時仍會討論聲學預期。",
      },
      {
        question: "直立鎖邊和明釘金屬有什麼區別？",
        answer:
          "直立鎖邊通過凸起接縫下方的夾具隱藏固定，允許板材熱脹冷縮並將墊圈螺釘置於天氣之外。明釘板成本較低，但需長期維護緊固件。高端住宅工程通常推薦直立鎖邊。",
      },
      {
        question: "可以在現有瓦片上直接鋪金屬屋頂嗎？",
        answer:
          "有時結構上可行，但通常更傾向拆除，以便檢查屋面板並正確建造墊層和泛水系統。困住舊覆蓋層也會使日後診斷更困難。",
      },
      {
        question: "金屬屋頂適合複雜屋頂造型嗎？",
        answer:
          "適合，但複雜程度決定金屬項目的成敗：屋脊、天溝、老虎窗和死衚衕細部需要熟練的金屬工藝並增加成本。幾何複核是判斷金屬是否適合住宅的第一步。",
      },
    ],
    publicPriceHint:
      "金屬重鋪價格取決於屋頂幾何形態、板材型材、墊層策略和穿透點複雜程度。請申請評估以獲取書面工程範圍。",
  },
  "flat-roofing": {
    title: "平屋頂更換：SBS / TPO / PVC",
    shortTitle: "平屋頂更換",
    eyebrow: "低坡重鋪",
    description: "使用 SBS、TPO、PVC 或其他指定膜材系統，完成住宅或商業低坡屋頂的整體重鋪。",
    definition:
      "平屋頂更換拆除或覆蓋現有低坡屋頂組件，並安裝根據排水、基層和項目要求選定的新膜材系統。",
    forWhom: "低坡屋頂出現反覆膜材失效、保溫受潮、系統過時，或配合外部翻修的業主。",
    warningSigns: [
      "多處膜材失效",
      "保溫層受潮",
      "系統過時或無法維護",
      "配合外部翻修工程",
      "長期積水且系統整體磨損",
    ],
    evaluates: ["膜材類型選項", "保溫和基層需求", "排水口和洩水口", "女兒牆和穿透點", "進場和保護"],
    causes: [
      "膜材使用壽命末期",
      "保溫飽和",
      "排水設計侷限",
      "紫外線和熱老化",
      "穿透點密集且細部失效累積",
    ],
    methodology: [
      "評估組件，在需要時調查受潮部位",
      "為項目選定膜材策略",
      "按範圍拆除或覆蓋",
      "按順序安裝保溫和膜材層",
      "將排水口、女兒牆和穿透點作為系統細部處理",
    ],
    materials: [
      "SBS、TPO、PVC 或其他指定膜材",
      "按範圍的保溫和蓋板",
      "排水和泛水配件",
      "與系統相符的膠粘劑、緊固件或熱工工藝",
    ],
    bcClimate: "大溫哥華多雨環境中，低坡屋頂更依賴排水設計和穩健的膜材細部，而非營銷標籤本身。",
    whenAppropriate: "當低坡組件已耗盡，或作為更廣泛項目的一部分進行更新時。",
    whenOtherService: "若膜材現場仍完好，局部膜材維修可能仍可行。",
    process: [
      "組件評估，在需要時進行受潮調查",
      "膜材系統選擇和書面工程範圍",
      "按範圍拆除或覆蓋現有組件",
      "按順序安裝保溫和膜材",
      "排水口、女兒牆和穿透點細部複核",
    ],
    solutionCategories: [
      {
        title: "膜材系統選擇",
        body: "根據項目條件選擇 SBS、TPO、PVC 或其他指定組件。",
      },
      {
        title: "組件更換",
        body: "按批准範圍處理膜材及相關層次。",
      },
      {
        title: "排水與細部",
        body: "排水口、女兒牆和穿透點作為核心防水工程處理。",
      },
    ],
    repairVsReplace: {
      repair: "若膜材現場仍可使用則進行維修。",
      replace: "當系統已耗盡時進行完整低坡更換。",
    },
    scopeFactors: ["面積和幾何形態", "膜材類型", "保溫需求", "穿透點密度", "進場和清運"],
    faqs: [
      {
        question: "使用 SBS、TPO 或 PVC 的平屋頂更換是什麼？",
        answer:
          "平屋頂更換拆除或覆蓋現有低坡屋頂組件，並安裝根據排水、基層和項目要求選定的新膜材系統。",
      },
      {
        question: "SBS、TPO 還是 PVC，哪種平屋頂膜材最好？",
        answer:
          "沒有一種普遍適用於所有情況的最佳選擇。雙層 SBS 是當地常用、冗餘度高的系統；TPO 和 PVC 為熱焊單層系統，重量較輕並有反射選項，PVC 還具有較強的耐化學性。基層、排水、屋頂使用方式和預算決定選擇。",
      },
      {
        question: "應該拆除還是覆蓋現有平屋頂？",
        answer:
          "若現有組件乾燥完好，覆蓋可節省成本和清運；但若保溫受潮或已有多個層次，拆除是更誠實的路徑。決策前的受潮調查可避免把問題封在裡面。",
      },
      {
        question: "平屋頂更換有多大幹擾？",
        answer:
          "預計會有噪音、熱熔或膠粘作業時的氣味、分階段材料上料和受保護的通道。住戶通常可以留在家中，書面工程範圍會事先說明工序、工作時間和保護措施。",
      },
      {
        question: "平屋頂更換時能改善排水嗎？",
        answer:
          "更換是糾正長期積水的合適時機：錐形保溫、增設或移位排水口以及洩水口工程，可以設計進新組件，而不是事後補救。",
      },
    ],
    publicPriceHint:
      "低坡更換價格取決於面積、膜材選擇、保溫需求、排水工程和穿透點密度。請申請評估。",
  },
  "roof-inspection-maintenance": {
    title: "屋頂健康檢查與維護",
    shortTitle: "健康檢查",
    eyebrow: "先看狀況",
    description: "檢查、拍照、少量密封膠或維護工作，以及供規劃參考的屋頂狀況報告。",
    definition:
      "屋頂健康檢查是對覆蓋層、泛水、排水及可接近相關組件的結構化目視評估，通常配合照片記錄和事先約定的有限維護項目。",
    forWhom: "準備買賣、計劃翻修、注意到早期警示跡象，或希望在雨季前建立有記錄基線的業主。",
    warningSigns: [
      "不確定屋頂年齡或剩餘狀況",
      "輕微水漬但來源不明",
      "季節性雜物和苔蘚積聚",
      "穿透點密封膠老化",
      "需要有記錄的計劃基線",
    ],
    evaluates: [
      "按屋面劃分的覆蓋層狀況",
      "泛水和穿透點",
      "排水和天溝接口",
      "可見通風組件",
      "維護與維修事項的優先排序",
    ],
    causes: [
      "正常老化但缺乏記錄",
      "維護拖延",
      "場地特定的天氣暴露",
      "質量不明的以往工程",
      "買賣或翻修決策需求",
    ],
    methodology: [
      "瞭解關注點和可獲取的歷史",
      "系統檢查外部屋頂區域",
      "拍攝代表性狀況照片",
      "僅完成約定的少量維護",
      "提供清晰的狀況摘要和選項",
    ],
    materials: [
      "照片記錄",
      "包含時的少量密封膠或緊固件",
      "若納入範圍則清理有限雜物",
      "書面狀況說明",
    ],
    bcClimate: "季節性降雨使入冬前和暴風雨後的健康檢查有助於發現堵塞排水、材料翹起和早期泛水失效。",
    whenAppropriate: "在承諾維修、更換或進一步調查前需要清晰瞭解狀況時。",
    whenOtherService: "主動嚴重漏水或暴風雨開口應走緊急或維修路徑。",
    process: [
      "預約健康檢查並說明任何關注點",
      "系統性外部檢查並拍照記錄",
      "上門期間完成約定的少量維護",
      "書面狀況摘要及排序後的優先事項",
      "就建議的維修進行後續討論",
    ],
    solutionCategories: [
      {
        title: "有記錄的檢查",
        body: "對屋頂組件上可見且可接近部分的結構化審閱。",
      },
      {
        title: "少量維護",
        body: "僅在納入範圍且適當時進行的有限密封膠或雜物工作。",
      },
      {
        title: "規劃摘要",
        body: "區分需立即處理的事項與中期監測事項。",
      },
    ],
    repairVsReplace: {
      repair: "許多發現是維護或局部維修事項，而非全面更換的觸發因素。",
      replace: "僅當觀察到的狀況支持更廣泛路徑時才討論更換。",
    },
    scopeFactors: [
      "屋頂大小和複雜程度",
      "進場條件",
      "照片記錄深度",
      "包含的維護項目",
      "物業上的屋頂系統數量",
    ],
    faqs: [
      {
        question: "什麼是屋頂健康檢查與維護？",
        answer:
          "屋頂健康檢查是對覆蓋層、泛水、排水及可接近相關組件的結構化目視評估，通常配合照片記錄和事先約定的有限維護項目。",
      },
      {
        question: "我應該多久檢查一次屋頂？",
        answer:
          "當地較有用的節奏是秋季降雨前檢查一次，並在重大風暴後再查。樹木覆蓋重的住宅，或屋頂已進入預期壽命後半段的住宅，值得更頻繁關注。",
      },
      {
        question: "健康檢查後我會收到什麼？",
        answer:
          "附有照片的覆蓋層、泛水、排水和通風狀況摘要，區分需及時維修的事項、需監測的事項和正常磨損。這是一份可按自己節奏採取行動的規劃文件。",
      },
      {
        question: "買賣房屋前做屋頂檢查值得嗎？",
        answer:
          "值得：屋頂是較昂貴的建築組件之一，有記錄的狀況基線能減少談判中的猜測。請注意這是屋頂狀況審閱，而非正式的購房驗房。",
      },
      {
        question: "檢查時發現的問題會當場修嗎？",
        answer:
          "事先約定的小維護，例如穿透點少量密封膠更新，可在上門時完成。較大發現會記錄並單獨確定範圍，讓您憑清晰信息做決定，而非現場施壓。",
      },
    ],
    publicPriceHint:
      "健康檢查範圍取決於屋頂大小和複雜程度、進場條件，以及包含多少記錄和少量維護。請申請評估。",
  },
} satisfies Record<ServiceSlug, ServiceContent>;

export const serviceAreaContent = {
  vancouver: {
    intro:
      "溫哥華住宅從緊湊型獨立屋到帶擴建、露臺和低坡部分的建築師設計住宅，各有不同。有效的屋頂評估應從建築的實際幾何形態出發，而不是套用統一系統。",
    localContext:
      "溫哥華市內的項目可能適用與卑詩省其他地區不同的要求。Revolin 會在最終確定工程範圍前，確認物業所屬轄區及相關項目要求。",
    assessmentFocus: [
      "原屋頂、擴建部分和低坡區域之間的交接",
      "天溝、牆體交接、穿透點和周邊泛水",
      "在緊湊城市地塊內必須有效運作的排水路徑",
    ],
    propertyContexts: ["獨立屋", "經翻修及擴建的住宅", "混合坡度屋頂系統"],
    faqs: [
      {
        question: "溫哥華屋頂項目與卑詩省其他地區遵循相同規定嗎？",
        answer:
          "不一定。溫哥華市有自己的建築附例體系。適用要求取決於地址和工程範圍，因此應在規劃時確認，而不是根據區域清單推定。",
      },
      {
        question: "一棟住宅會需要不止一種屋頂做法嗎？",
        answer:
          "會。擴建、門廊和屋頂露臺可能帶來與主屋頂不同的坡度和交接。評估時應將這些連接視為一個整體的水管理系統。",
      },
    ],
  },
  burnaby: {
    intro:
      "本拿比住宅社區包括平坦地塊、山坡街道，以及經過擴建而變化的房屋。這種多樣性使排水、安全進場和屋頂各部分的連接成為初次評估的重要內容。",
    localContext:
      "由於暴露條件、樹木覆蓋、坡度和建築形態因地而異，本拿比相隔幾個社區的屋頂表現也可能不同。建議以物業現場觀察到的狀況為依據。",
    assessmentFocus: [
      "住宅周圍的坡度、進場及材料搬運限制",
      "天溝、檐口、檐溝和低位屋面的排水",
      "擴建部分與原建築相接的位置",
    ],
    propertyContexts: ["山坡物業", "帶擴建部分的住宅", "成熟住宅區地塊"],
    faqs: [
      {
        question: "為什麼現場進場條件會影響屋頂工程範圍？",
        answer:
          "進場條件影響保護措施、材料搬運和施工順序。應在最終確定安裝計劃和工程範圍前進行檢查。",
      },
      {
        question: "檐溝應與屋頂一併檢查嗎？",
        answer:
          "排水應視為從屋面到合適排放點的連續路徑。評估會記錄可見的檐溝和落水管狀況，但不會假定已包含可選的排水工程。",
      },
    ],
  },
  coquitlam: {
    intro:
      "高貴林物業可能同時具有坡地、多屋面住宅和鄰近植被。建議維修或更換前，會綜合檢查屋頂狀況、雜物路徑、排水及風雨暴露。",
    localContext:
      "高貴林不同社區的海拔和暴露條件可能變化。合適的屋頂系統和細部因此取決於具體建築，而不是全市統一的材料規定。",
    assessmentFocus: [
      "多層住宅由上層到下層屋面的排水",
      "複雜屋頂形態的天溝和屋頂與牆體交接",
      "檐溝、穿透點和排水位置周邊的雜物積聚",
    ],
    propertyContexts: ["多層住宅", "坡地住宅", "鄰近成熟植被的住宅"],
    faqs: [
      {
        question: "複雜屋頂一定需要更換嗎？",
        answer:
          "不一定。複雜程度會影響診斷和細部處理，但維修還是更換仍取決於觀察到的問題是局部還是系統性的。",
      },
      {
        question: "檢查時如何考慮樹木雜物？",
        answer: "評估可記錄雜物積聚或妨礙排水的位置，再根據物業現場可見情況提出維護建議。",
      },
    ],
  },
  surrey: {
    intro:
      "素裏涵蓋多種住宅環境，從成熟社區到擁有大型複雜屋頂的新住宅。屋頂面積、暴露條件和交接部位數量都會影響實際工程範圍。",
    localContext:
      "由於全市狀況各異，不能將一處素裏物業的建議當作另一處的標準規格。Revolin 會記錄現場屋頂狀況並說明取捨。",
    assessmentFocus: [
      "大型屋面以及各屋面之間的排水方式",
      "老虎窗、牆體、煙囪和穿透點處的泛水",
      "損壞是局部還是在屋頂系統內重複出現",
    ],
    propertyContexts: ["大型獨立屋", "較新的多屋面屋頂", "成熟社區住宅"],
    faqs: [
      {
        question: "大型屋頂維修還是更換由什麼決定？",
        answer:
          "面積本身不能決定。決定因素包括覆蓋層狀況、失效點的數量和位置、交接細部，以及能否實現耐久的局部維修。",
      },
      {
        question: "評估可以只限於一個問題區域嗎？",
        answer:
          "我們會重點檢查所報告的問題，但也應查看相鄰屋面和排水路徑，因為水可能移動後纔在室內顯現。",
      },
    ],
  },
  richmond: {
    intro:
      "列治文整體地勢較低，長時間降雨時，有序的屋頂排水尤其明顯。低坡區域、周邊邊緣和排放路徑應與主要屋面覆蓋層得到同等重視。",
    localContext:
      "沿海暴露和場地開闊程度會影響風雨到達屋頂邊緣和交接部位的方式。只有檢查住宅坡度、形態和暴露條件後，纔會提出材料和細部建議。",
    assessmentFocus: [
      "低坡區域的有效排水和可見低點",
      "暴露於風雨的屋頂邊緣、泛水和交接部位",
      "檐溝和落水管如何承接各屋面的雨水",
    ],
    propertyContexts: ["低坡屋頂區域", "開闊住宅地塊", "現代獨立屋"],
    faqs: [
      {
        question: "看起來平坦的屋頂真的是平的嗎？",
        answer:
          "低坡屋頂仍應將水導向排水位置。評估會查明設計排水路徑，以及水未按預期流動的可見跡象。",
      },
      {
        question: "沿海環境是否意味着金屬一定是正確選擇？",
        answer:
          "不是。屋頂坡度、系統設計、產品適用性、細部和業主重點都很重要。不能僅憑位置選擇材料。",
      },
    ],
  },
  "north-vancouver": {
    intro:
      "北溫哥華住宅常位於陡峭、溼潤且植被茂密的環境。確定屋頂工程範圍前，需切實考慮安全進場、集中徑流和排水點周邊雜物。",
    localContext:
      "低海拔社區與靠近山地的物業條件可能不同。評估會記錄具體場地的暴露和屋頂進場情況，而不是套用通用的北岸規格。",
    assessmentFocus: [
      "陡峭或高位屋面的安全進場",
      "不同屋頂層級之間的大流量排水路徑",
      "陰影屋面周邊的雜物及水分滯留",
    ],
    propertyContexts: ["陡坡地塊", "錯層住宅", "林木環繞的住宅物業"],
    faqs: [
      {
        question: "陰影較多的屋頂會需要不同的維護計劃嗎？",
        answer:
          "可能會。乾燥條件和雜物積聚因朝向和樹木覆蓋而異，因此維護指導應以觀察到的屋面狀況為依據。",
      },
      {
        question: "陡坡地塊如何影響項目？",
        answer:
          "它會影響進場、保護和施工順序。這些限制會在最終確定工程範圍前記錄，而不會留到施工時才處理。",
      },
    ],
  },
  "west-vancouver": {
    intro:
      "西溫哥華住宅經常採用複雜體量、大面積玻璃、露臺，以及陡坡和低坡屋頂的組合。成功的屋頂工程必須解決各處交接，同時兼顧建築設計。",
    localContext:
      "臨海、山坡和林木環繞的物業，即使在同一市區也可能有不同暴露條件。Revolin 會先評估具體屋頂幾何形態、進場和水流路徑，再建議系統。",
    assessmentFocus: [
      "建築金屬、膜材和坡屋頂區域之間的連接",
      "女兒牆、屋頂露臺、牆體接口和隱蔽排水",
      "已完成室外空間周邊的進場和保護規劃",
    ],
    propertyContexts: ["建築師設計住宅", "山坡住宅", "混合材料屋頂系統"],
    faqs: [
      {
        question: "屋頂更換能保留住宅的建築特色嗎？",
        answer:
          "這應是建議的一部分。可在評估技術適用性的同時，考慮輪廓、顏色、邊緣細部和可見交接。",
      },
      {
        question: "為什麼要一併評估混合屋頂系統？",
        answer: "水會跨越不同屋面之間的邊界。適用於一個表面的細部，仍必須可靠連接至下一個系統。",
      },
    ],
  },
  "new-westminster": {
    intro:
      "新威斯敏斯特有較老住宅、翻修住宅和緊湊住宅地塊，多代屋頂工程可能在此相接。首要任務是了解現有系統以及後期改動與之連接的位置。",
    localContext:
      "房齡本身不能決定屋頂狀況。Revolin 會查看覆蓋層、泛水、交接和排水點的證據，再區分可維修細部與更廣泛的系統問題。",
    assessmentFocus: [
      "屋面可見的過往多層維修或改動",
      "擴建、門廊和附屬結構處的交接",
      "緊湊地塊的材料進場和鄰近物業保護",
    ],
    propertyContexts: ["較老的獨立屋", "經翻修的住宅", "緊湊住宅地塊"],
    faqs: [
      {
        question: "較老住宅一定需要整體重鋪屋頂嗎？",
        answer: "不一定。可見狀況和系統性能比住宅本身的房齡更重要。作出決定前需要有記錄的評估。",
      },
      {
        question: "如果屋頂有多處過往修補怎麼辦？",
        answer:
          "診斷會記錄以往工程。同一區域反覆維修可能指向未解決的交接問題，而互不相關的修補可能各有原因。",
      },
    ],
  },
  "port-coquitlam": {
    intro:
      "高貴林港住宅常將簡潔坡屋頂與後期增加的車庫、擴建或有頂戶外空間組合。這些次要屋面會改變水流到天溝、牆體和檐溝的方式。",
    localContext:
      "擴建處附近出現漏水，不代表源頭一定就在正上方。評估會先追蹤屋面和交接部位，再界定維修區域。",
    assessmentFocus: [
      "主屋頂、車庫和後期擴建之間的連接",
      "承接上層屋面排水的低位屋面",
      "屋頂與牆體交接處的穿透點和泛水",
    ],
    propertyContexts: ["帶附屬車庫的住宅", "經擴建的獨立屋", "多層級屋頂排水"],
    faqs: [
      {
        question: "為什麼漏水會出現在遠離源頭的位置？",
        answer:
          "水可能沿屋面板、結構構件或屋頂交接處移動後才顯現。診斷會追查可能路徑，而不是把室內水漬當作源頭。",
      },
      {
        question: "車庫和門廊屋頂屬於評估範圍嗎？",
        answer:
          "可以屬於，尤其當它們與主屋頂相連或承接主屋頂排水時。所要求的評估範圍應明確需要檢查的區域。",
      },
    ],
  },
  "port-moody": {
    intro:
      "滿地寶臨海灣及林木環繞的住宅環境，會使同一屋頂不同朝向受到不同的溼氣、雜物和暴露影響。檢查記錄應按屋面區分這些狀況。",
    localContext:
      "屋頂朝向、附近植被和建築高度可能與材料名稱同樣重要。Revolin 會先記錄這些變量，再討論維護、維修或更換方案。",
    assessmentFocus: [
      "邊緣、牆體和穿透點處的風雨",
      "天溝和排水點的雜物積聚",
      "陰影和暴露屋面的不同風化模式",
    ],
    propertyContexts: ["面向海灣的物業", "林木環繞的地塊", "多屋面獨立屋"],
    faqs: [
      {
        question: "同一屋頂不同朝向會以不同速度老化嗎？",
        answer:
          "會。日照、陰影、風和雜物暴露因朝向而異。狀況報告應分別描述各屋面，而不是為整個屋頂指定單一狀況。",
      },
      {
        question: "表面雜物能證明屋頂正在失效嗎？",
        answer: "不能。它屬於維護和排水觀察，本身不是更換診斷。仍需評估下方覆蓋層和細部。",
      },
    ],
  },
  "maple-ridge": {
    intro:
      "楓樹嶺包括較大住宅物業、林木社區和山麓地塊，各處暴露和進場條件差異很大。屋頂及其周邊條件會作為一個項目計劃整體檢查。",
    localContext:
      "較高或更暴露的地塊，可能遇到與低處社區不同的條件。因此，系統選擇和細部以物業為依據，而不是區域推定。",
    assessmentFocus: [
      "較大或坡地現場的進場、材料佈置和保護",
      "成熟植被附近的排水和雜物管理",
      "屋脊、檐口和屋頂邊緣的暴露",
    ],
    propertyContexts: ["較大住宅地塊", "山麓社區", "林木環繞的物業"],
    faqs: [
      {
        question: "較大的物業會改變屋頂建議嗎？",
        answer:
          "本身不會。它可能改變進場和項目物流，而屋頂系統建議仍取決於坡度、系統、暴露條件和狀況。",
      },
      {
        question: "如何檢查暴露程度較高的屋頂邊緣？",
        answer:
          "評估會記錄可見邊緣狀況、與固定相關的問題及交接細部，再將建議與所選系統和適用要求相聯繫。",
      },
    ],
  },
  delta: {
    intro:
      "三角洲的社區包括沿海、農業區邊緣和內陸住宅環境。不同暴露條件意味着選擇方案前，必須評估具體地塊的風、排水和屋頂幾何形態。",
    localContext:
      "不能只憑市區名稱為措瓦森、拉德納或北三角洲的物業提出建議。Revolin 會根據該地址實際觀察到的建築和條件確定工程範圍。",
    assessmentFocus: [
      "檐口、屋脊和周邊細部的風力暴露",
      "大面積屋面的排水能力和排放",
      "不同暴露立面上的材料狀況",
    ],
    propertyContexts: ["沿海社區", "開闊住宅地塊", "內陸獨立屋"],
    faqs: [
      {
        question: "同一種屋頂系統適合三角洲所有地區嗎？",
        answer: "沒有自動適用全市的選擇。坡度、暴露、建築、排水和預算都會影響合適的系統。",
      },
      {
        question: "強風後會檢查什麼？",
        answer:
          "針對性評估可查看可接近的屋頂邊緣、覆蓋層、泛水和穿透點是否有可見變化。安全優先；業主不應爬上溼滑或受損屋頂。",
      },
    ],
  },
  langley: {
    intro:
      "蘭裏物業可能包括較大獨立屋、寬闊屋面和多個相連結構。清晰的工程範圍會區分各屋面，同時解決它們如何排水和連接。",
    localContext:
      "開闊地塊與成熟林木社區有不同的維護和暴露考量。Revolin 會記錄現場實際狀況，再建議材料或維修界限。",
    assessmentFocus: [
      "寬闊屋面和較長排水路徑",
      "住宅、車庫和有頂戶外區域之間的連接",
      "開闊暴露區域與樹木遮蔽區域的差異",
    ],
    propertyContexts: ["較大獨立屋", "帶附屬結構的住宅", "開闊住宅地塊"],
    faqs: [
      {
        question: "項目可以按屋面分階段進行嗎？",
        answer:
          "有時可以，但前提是系統和交接部位允許設置耐久的停工點。工程範圍應說明分階段工作如何影響相連區域。",
      },
      {
        question: "寬闊屋面需要某一種特定材料嗎？",
        answer:
          "不需要。幾何形態只是因素之一。必須綜合考慮坡度、系統設計、暴露、外觀、維護重點和預算。",
      },
    ],
  },
  "white-rock": {
    intro:
      "白石鎮住宅可能兼具臨海暴露、現代屋頂形態、陽臺和低坡區域。邊緣、牆體交接和排水細部會結合建築設計一併檢查。",
    localContext:
      "沿海條件並不意味着某一種屋頂產品普遍正確。會結合坡度和業主重點，考慮產品適用性、系統設計和可見細部。",
    assessmentFocus: [
      "暴露於風雨的周邊和交接細部",
      "女兒牆、基座和相鄰牆體周邊的低坡排水",
      "可見金屬細部與所選系統的兼容性",
    ],
    propertyContexts: ["面海住宅", "現代住宅", "混合坡度屋頂設計"],
    faqs: [
      {
        question: "海洋暴露會自動排除某種屋頂材料嗎？",
        answer:
          "不會自動排除。必須針對物業檢查完整系統、產品限制、塗層或表面處理、固定方式和交接細部。",
      },
      {
        question: "為什麼陽臺和女兒牆連接很重要？",
        answer: "它們可能中斷簡單的排水，並引入牆體、基座或膜材交接。這些接口應納入有記錄的評估。",
      },
    ],
  },
} satisfies Record<ServiceAreaSlug, ServiceAreaContent>;

export const ui = {
  common: {
    home: "首頁",
    services: "服務項目",
    serviceAreas: "服務區域",
    requestAssessment: "申請屋頂評估",
    exploreServices: "瀏覽屋頂服務",
    learnMore: "了解更多",
    nextStep: "下一步",
    questions: "問題",
    call: "致電",
  },
  serviceDetail: {
    breadcrumbLabel: "麪包屑導航",
    whoFor: "適用對象",
    conditionFirstTitle: "先看狀況，再選產品",
    warningSigns: "常見警示跡象",
    assessmentDiscipline: "嚴謹評估",
    evaluatesTitle: "Revolin 的評估內容",
    solutionCategories: "方案類別",
    solutionsTitle: "方案應依據屋頂，而不是套用腳本",
    repairMakesSense: "適合考慮維修的情況",
    replacementMakesSense: "適合考慮更換的情況",
    scope: "工程範圍",
    scopeTitle: "影響工程的因素",
    scopeBody:
      "實際工程範圍在評估後確定。Revolin Roofing 不公佈統一價格、壽命或系統，因為這些取決於建築和所選系統。",
    beforeDecide: "決定之前",
    assessmentTitle: "申請清晰的屋頂評估",
    assessmentBody: "請告訴我們您觀察到的情況及物業位置。我們會利用這些信息安排合適的下一步溝通。",
  },
  serviceArea: {
    regions: { metroVancouver: "大溫哥華", lowerMainland: "低陸平原" },
    roofingServicesSuffix: "屋頂服務",
    titlePrefix: "屋頂服務地點：",
    confirmedCommunity: "是已確認的服務社區。這是服務區域頁面，並不表示當地設有分公司。",
    localContext: "當地屋頂環境",
    propertyDecides: "建議取決於具體物業",
    propertyContexts: "規劃時考慮的物業類型",
    assessmentPriorities: "評估重點",
    attentionPrefix: "在此地值得重點關注的事項：",
    planningDisclaimer:
      "這是一般規劃信息，並非遠程診斷或工程意見。檢查結果和適用要求將針對具體物業確認。",
    relatedServices: "相關服務",
    decisionsPrefix: "住宅屋頂決策：",
    relatedBody:
      "從問題和觀察到的屋頂狀況出發。合適的下一步可能是針對性維修、預防性評估或計劃更換。",
    genericService: "屋頂服務",
    faqSuffix: "常見問題",
    faqTitle: "評估前的常見問題",
    requestAssessment: "申請評估",
    assessmentTitle: "從眼前的屋頂開始。",
    assessmentBodyPrefix:
      "請告訴我們該物業、屋頂區域及您注意到的情況。相關信息有助於確定評估重點，但不能替代現場檢查：",
    contactOptions: "查看所有聯繫方式",
    serviceNames: {
      roofRepair: "屋頂維修",
      roofReplacement: "屋頂更換",
      asphaltShingles: "建築型瀝青瓦",
      metalRoofing: "金屬屋頂",
      flatRoofing: "平屋頂與低坡屋頂",
      inspectionMaintenance: "屋頂檢查與維護",
    },
  },
  servicesIndex: {
    eyebrow: "屋頂服務",
    title: "正確的工程範圍始於正確的診斷",
    body: "了解 Revolin Roofing 的核心服務類別。每項服務都從建築、屋頂系統及可觀察狀況出發，而不是預先設定產品建議。",
    exploreService: "了解服務",
    technicalContext: "技術背景",
    sourcesTitle: "應參考卑詩省的最新資料",
    roofingManual: "RCABC 屋頂施工規範手冊",
    roofingManualBody: "現行屋頂施工參考資料；提供鏈接不表示 Revolin 是會員或提供 RoofStar 保證。",
    bcCodes: "卑詩省法規",
    bcCodesBody: "適用於相關轄區和項目範圍的省級法規、修訂及公告。",
    vancouverBylaw: "溫哥華建築附例",
    vancouverBylawBody: "溫哥華的特定要求需與省級法規分開覈查。",
  },
  areaIndex: {
    breadcrumbLabel: "麵包屑導覽",
    eyebrow: "大溫哥華和低陸平原",
    title: "屋頂服務區域",
    body: "Revolin Roofing 為以下社區的住宅屋頂提供評估服務。每個專案均根據物業的屋頂形式、暴露條件、進場條件及適用的當地要求進行規劃。",
    coverageEyebrow: "服務範圍，而非分公司",
    coverageTitle: "提供當地背景，不虛構辦公地點",
    coverageBody:
      "這些頁面說明可能提供屋頂評估服務的地區，並不表示 Revolin Roofing 在每個城市都設有辦公室。請提供物業地址，以確認專案服務範圍。",
    nearbyEyebrow: "附近的社區？",
    nearbyTitle: "請聯絡我們確認專案服務範圍。",
    contactCta: "聯絡 Revolin Roofing",
  },
  corePages: {
    about: {
      eyebrow: "Revolin Group 旗下公司",
      title: "以建築公司的整體視角提供屋頂專業服務",
      body: "Revolin Roofing 是 Revolin Group 的專業屋頂部門，將母品牌剋制的建築審美、透明流程和嚴謹項目思維帶入屋頂系統。",
      relationship: "品牌關係",
      relationshipTitle: "專注屋頂，着眼整棟建築。",
      relationshipBody: [
        "屋頂不是孤立表面。它與牆體、外牆、天窗、通風、排水和室內系統相接。Revolin Roofing 會結合更廣泛的建築背景處理這些接口。",
        "這一視角有助於更清楚地診斷和界定工程範圍。當狀況超出屋頂工程時，仍需相應工程師、設計師或專業人員參與；整體視角有助於更早識別這些界限。",
      ],
      principles: "工作原則",
      principlesTitle: "在每個決策點保持清晰",
      principleItems: [
        {
          title: "先評估，再建議",
          body: "提出工程範圍前，會綜合考慮可見症狀、可能源頭及更廣泛的系統狀況。",
        },
        {
          title: "尊重建築設計",
          body: "材料、顏色、輪廓、邊緣和交接會影響建築觀感，而不只是排水錶現。",
        },
        {
          title: "說明限制",
          body: "假設、排除項及需進一步檢查的狀況，都應在溝通和工程範圍中明確說明。",
        },
      ],
      parentCompany: "母公司",
      parentTitle: "了解 Revolin Group",
      parentBody: "如需翻修、施工及更廣泛的建築工程，請訪問母公司網站。",
      parentLink: "訪問 RevolinGroup.com",
    },
    gallery: {
      eyebrow: "施工前後",
      title: "大溫哥華屋頂施工前後對比",
      body: "探索 Revolin Roofing 為大溫哥華與低陸平原業主打造的施工前後圖庫：從緊急漏水維修到瀝青瓦、金屬、雪松木瓦與平屋頂更換，每組圖庫呈現問題、診斷與完工後的屋頂背景。",
      notice:
        "可按服務、屋頂類型或城市篩選，找到最貼近您住宅的案例，然後申請屋頂評估以獲得針對您物業的建議。",
      projectHeroBody:
        "探索 Revolin Roofing 在大溫哥華的施工前後變化：從緊急漏水維修到瀝青瓦、金屬、雪松木瓦與平屋頂的完整更換。每組圖庫突出低陸平原業主常見的問題、診斷重點與完工後的屋頂效果。",
    },
    faq: {
      eyebrow: "業主指南",
      title: "不帶銷售話術的屋頂問題解答",
      body: "有關評估、維修、更換及屋頂系統的實用信息。針對具體項目的建議需在現場檢查後提供。",
    },
    contact: {
      eyebrow: "聯繫 Revolin Roofing",
      title: "告訴我們您觀察到的情況",
      body: "請提供物業位置、已知屋頂類型，以及您關注的問題或考慮中的項目。只有在已配置的客戶諮詢服務接受提交後，表單纔會確認送達。",
      direct: "直接聯繫",
      basics: "從基本信息開始",
      phone: "電話",
      email: "電子郵件",
      serviceRegion: "服務區域",
      regionValue: "大溫哥華和低陸平原",
    },
    legal: {
      legal: "法律信息",
      privacyTitle: "隱私政策",
      termsTitle: "網站使用條款",
      privacyBody: "網站諮詢、歸因數據和必要瀏覽器存儲的處理方式。",
      termsBody: "使用 Revolin Roofing 網站及一般教育內容時適用的條款。",
      lastUpdated: "最後更新：2026 年 8 月 10 日。",
      privacySections: [
        {
          title: "您提供的信息",
          body: "提交諮詢時，表單可能收集您的姓名、電話號碼、電子郵件地址、郵政編碼、所需服務、首選時間和留言。只有在配置獲批的客戶諮詢遞送服務後，才能提交。",
        },
        {
          title: "歸因及技術信息",
          body: "網站可能在會話存儲中保存 UTM 值和 Google 廣告點擊標識符等活動參數，以確定諮詢來源。分析事件不得包含表單中輸入的私人信息。",
        },
        {
          title: "信息用途",
          body: "諮詢信息用於回覆屋頂需求、了解項目需要並維護相關業務記錄。本政策不表示或授權出售任何個人信息。",
        },
        {
          title: "服務提供商與保留",
          body: "獲批的託管、分析和客戶諮詢遞送提供商可能代表 Revolin 處理信息。信息保留和訪問僅限於正當的業務、法律及安全需要。",
        },
        {
          title: "您的選擇",
          body: "您可聯繫 Revolin Group，查詢與諮詢相關的個人信息。也可通過瀏覽器設置清除瀏覽器存儲。",
        },
      ],
      termsSections: [
        {
          title: "一般信息",
          body: "網站內容僅供一般教育和規劃參考，不構成工程意見、建築法規認定、檢查證書或針對具體項目的建議。",
        },
        {
          title: "項目工程範圍",
          body: "屋頂建議、價格、工期、產品選擇、保修條款和排除項，僅在已簽署項目文件中載明時適用。網站示例不構成對特定物業的承諾。",
        },
        {
          title: "圖片和項目記錄",
          body: "網站圖片與項目資料僅供一般規劃參考。工程範圍、保修與承諾僅在已簽署的項目文件中載明時適用。",
        },
        {
          title: "外部鏈接",
          body: "外部網站鏈接僅供參考，其可用性、內容和隱私做法由各自運營方控制。",
        },
        {
          title: "可接受使用",
          body: "不得濫用網站、嘗試未經授權的訪問，或通過表單提交非法、欺騙性或惡意材料。",
        },
      ],
    },
    assessment: {
      title: "申請屋頂評估",
      body: "請提供物業位置，並簡要說明屋頂或所關注的問題。",
    },
  },
} as const;

export const services = serviceContent;
export const serviceAreas = serviceAreaContent;

export default { services, serviceAreas, ui } as const;
